import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Sparkles, X, Maximize2, Minimize2, Loader2, ChevronRight, ChevronDown,
  CheckCircle2, XCircle, Clock, SkipForward, ArrowDown, ArrowUp, Square, Circle,
  Plus, MoreHorizontal, ListChecks, PlayCircle, Info, Smartphone,
  SlidersHorizontal, PanelLeftClose, FileText, BarChart3, Search,
} from 'lucide-react'
import { useAutoAgent, type TodoStatus } from '@/hooks/useAutoAgent'
import { useClaudeAgent } from '@/hooks/useClaudeAgent'
import type { ConnectionStatus } from '@/hooks/useClaudeAgent'
import { useCodexAgent } from '@/hooks/useCodexAgent'
import { useProjectStore } from '@/store/projectStore'
import { useDockUiStore } from '@/store/dockUiStore'
import { useBuilderContextStore } from '@/store/builderContextStore'
import { useDockBridgeStore, DOCK_PREFILL_EVENT } from '@/store/dockBridgeStore'
import toast from 'react-hot-toast'
import { getSettings, getLlmBundle, getRecentPrompts } from '@/api/settings'
import { runUnitTestOnce } from '@/api/unitTestAgent'
import {
  getPendingBuild, getPendingAttachments, clearPendingBuild, getPendingReviewFirst,
} from '@/lib/builderHandoff'
import type { UserSettings, LlmSettingsBundle } from '@/types'
import { AGENT_DISPLAY_NAME } from '@/data/agentBranding'
import AgentMessage, { UserBubble, AssistantText } from '@/components/common/AgentMessage'
import type { AgentMessage as CliMessage } from '@/hooks/useClaudeAgent'
import {
  WorkingCard, TurnFooter, DockAlert, ReviewCard, PlanProgressCard,
  groupTurns, dedupeSteps, useElapsed,
  type DockStep, type TurnUsage,
} from './dockTurns'
import AgentAskBubble from '@/components/agent/AgentAskBubble'
import { withConnectionMarker } from '@/components/connections/ConnectionDefineFlow'
import ScopeConfirmation from '@/components/agent/ScopeConfirmation'
import SourceTablePicker from '@/components/agent/SourceTablePicker'
import PlansPanel, { type PlanItem } from '@/components/agent/PlansPanel'
import DockPlansSlot from '@/components/agent/DockPlansSlot'
import { listPlans, deletePlan as deletePlanApi } from '@/api/agentplans'
import { useConfirm } from '@/components/common/ConfirmDialog'
import AttachmentChips from '@/components/common/AttachmentChips'
import IOFlowReferencePicker from '@/components/agent/IOFlowReferencePicker'
import AddSourceModal, {
  SOURCE_TYPES, type AttachedSource, type SourceKind,
} from '@/components/sources/AddSourceModal'
import AddSourceButton from '@/components/sources/AddSourceButton'
import { useAttachments } from '@/hooks/useAttachments'
import { ATTACHMENT_ACCEPT_WITH_DESIGN, toWire } from '@/lib/attachments'
import type { Attachment } from '@/types'
import ModelSettingsModal from './ModelSettingsModal'
import ContinueOnPhoneModal from './ContinueOnPhone'

// The message box rests at two lines (1rem × 1.5 line-height) and grows with
// the text up to TEXTAREA_MAX_HEIGHT before it scrolls.
const TEXTAREA_BASE_HEIGHT = 48
const TEXTAREA_MAX_HEIGHT = 200

const PROJECT_CONTEXT_PREFIXES = [
  '/workbench',
  '/generateapp/finetune',
  '/generateapp/preview',
  '/designers/',
  '/srceditor/',
  '/deploy/',
  '/documents/',
  '/builder/',
]
/** The left panel's on-screen name. */
const DOCK_TITLE = 'twasta.ai agent'

function isProjectContext(pathname: string) {
  return PROJECT_CONTEXT_PREFIXES.some((p) => pathname.startsWith(p))
}

// ---------------------------------------------------------------------------
// Top-level dock — picks the agent backend based on generation_mode and stays
// mounted across routes so chat state survives navigation.
// ---------------------------------------------------------------------------

export default function AskAIDock() {
  const { currentProject } = useProjectStore()
  if (!currentProject) return null
  return (
    <ModeRouter
      project={currentProject.application.folder_name}
      projectTitle={currentProject.application.title}
    />
  )
}

interface RouterProps {
  project: string
  projectTitle: string
}

// Payload of the `twasta:agent-create-object` event dispatched by Builder
// Mode's ObjectSpecPane when the user clicks "Create" on an ungenerated object.
interface CreateObjectDetail {
  project: string
  objName: string
  objType: string
  title?: string
  description?: string
  functionalSpecs?: string
  designReferenceUrl?: string
}

// Payload of the `twasta:agent-run-unit-test` event dispatched by the
// Transaction Designer's "Unit Test" button. The agent dock turns it into a
// project_modify request that delegates to the unit-test-runner sub-agent
// (Claude Code) or runs the same data-first protocol inline (Codex).
interface RunUnitTestDetail {
  project: string
  objName: string
  objType?: string
  title?: string
}

// The natural-language request the dock sends to the agent. The project_modify
// system prompt owns the routing (delegate vs inline) and the data-first /
// ask-before-model-change protocol — this just states the intent clearly.
function buildUnitTestPrompt(d: RunUnitTestDetail): string {
  const label = d.title || d.objName
  return (
    `Unit test the "${label}" form (transaction object ${d.objName}, type ` +
    `${d.objType || 'T'}). Run the full add/list/load/edit/delete test. Fix any ` +
    `test-DATA problems yourself — look up valid lookup / cross-reference values ` +
    `and seed a master row if one is missing — then re-run. If the data is valid ` +
    `but a validation still fails, that's a likely bug: ask me before changing ` +
    `the model. Report the final PASS/FAIL.`
  )
}

const OBJ_TYPE_NOUN: Record<string, string> = {
  T: 'form',
  S: 'page',
  V: 'visual',
  D: 'dashboard',
  R: 'report',
  G: 'process',
}

// Detect a BULK-BUILD / full-generation request ("create all the missing
// objects", "build everything", "generate the remaining models"). Generation
// always runs through the AutoAgent orchestrator (plan → per-object review),
// NOT the free-form Claude/Codex change agent — so when this matches in those
// docks we hand the request off to the orchestrator dock.
//
// MUST stay in sync with the backend `_is_build_all_request` in
// auto_agent_orchestrator.py (same verbs / quantifiers / nouns), so the dock's
// routing decision and the orchestrator's own pre-check agree.
export function isBuildAllRequest(message: string): boolean {
  const m = (message || '').trim().toLowerCase()
  if (!m) return false
  const genWord = [
    'generat', 'build', 'finish', 'complete', 'create', 'make',
    'author', 'scaffold', 'produce',
  ].some((w) => m.includes(w))
  if (!genWord) return false
  const padded = ` ${m} `
  const hasQuantifier =
    padded.includes(' all ') || padded.includes(' every ')
    || m.includes('remaining') || m.includes('missing') || m.includes('pending')
  const hasNoun = [
    'object', 'model', 'component', 'application', 'transaction', 'app structure',
  ].some((w) => m.includes(w))
  const standalone = [
    'everything', 'the rest', 'rest of', 'whole app', 'entire app',
    'all of it', 'all of them',
  ].some((p) => m.includes(p))
  return standalone || (hasQuantifier && hasNoun)
}

function ModeRouter({ project, projectTitle }: RouterProps) {
  const [mode, setMode] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  // When the user lands here from "Build it" on the Builder Hub, there's a
  // pending first-generation build. The full generation pipeline always runs
  // through the AutoAgent orchestrator (regardless of the configured
  // generation_mode, which only governs ongoing *change* requests), so force
  // the orchestrator-backed dock while that build is in flight.
  //
  // Derived per-project via refs (not useState/key) so it (a) re-evaluates
  // when the project changes but (b) stays stable for a given project — once
  // the autostart consumes/clears the pending flag, the dock must NOT flip to
  // the other backend mid-generation. Avoids re-fetching settings on every
  // project switch (which a key= would cause).
  const projectRef = useRef<string | null>(null)
  const forceOrchestratorRef = useRef(false)
  // A bulk-build request typed in the free-form Claude/Codex dock hands off to
  // the orchestrator at runtime (generation always uses the orchestrator). The
  // message is stashed for AutoAgentDock to send once it mounts.
  const [runtimeOrchestrator, setRuntimeOrchestrator] = useState(false)
  const handoffMsgRef = useRef<string | null>(null)
  if (projectRef.current !== project) {
    projectRef.current = project
    forceOrchestratorRef.current = getPendingBuild(project) !== null
    // New project → drop any pending handoff from the previous one.
    handoffMsgRef.current = null
  }

  // Keep the dock on the AutoAgent ORCHESTRATOR while a build/sample-data plan
  // is still in progress (paused at a phase, awaiting review, running, or
  // errored) — i.e. NOT completed. The orchestrator is the only backend that
  // can resume that plan AND, via its AI router, decide PER MESSAGE whether the
  // user is continuing THIS plan or asking for separate/new work. (Forcing it
  // is NOT "always resume": the backend still matches each message to the plan.)
  // Otherwise generation_mode=claude_code sends "continue" to the Claude change
  // agent, which re-evaluates the project from disk and leaves no Continue
  // control. `null` = not yet known — don't commit to a CLI dock until we do.
  const [activeBuild, setActiveBuild] = useState<boolean | null>(null)
  useEffect(() => {
    let mounted = true
    setActiveBuild(null)
    listPlans(project)
      .then((plans) => {
        if (!mounted) return
        setActiveBuild(plans.some((p) =>
          p.backend === 'autoagent'
          && (p.kind === 'build' || p.kind === 'sample_data')
          && p.status !== 'completed'
        ))
      })
      .catch(() => { if (mounted) setActiveBuild(false) })
    return () => { mounted = false }
  }, [project, reloadKey])

  const forceOrchestrator =
    forceOrchestratorRef.current || runtimeOrchestrator || activeBuild === true

  // Called by the free-form docks when the user asks to build all/missing
  // objects. Stash the message and flip to the orchestrator dock, which runs
  // the plan → per-object-review pipeline (and surfaces the phase tracker).
  const requestOrchestratorHandoff = useCallback((message: string) => {
    handoffMsgRef.current = message
    setRuntimeOrchestrator(true)
    toast('Building multiple objects — switching to the build planner so each is generated and reviewed.', {
      icon: '🗂️', duration: 4500,
    })
  }, [])

  // A runtime handoff to the orchestrator is per-project — reset it when the
  // project changes so a new project respects its configured agent mode.
  useEffect(() => {
    setRuntimeOrchestrator(false)
  }, [project])

  useEffect(() => {
    let mounted = true
    // Resolve the backend for the "project_modify" task. The new bundle
    // wins when it exists; otherwise fall back to the legacy fields so
    // older accounts that haven't migrated yet still route correctly.
    async function resolve() {
      try {
        const bundle = await getLlmBundle()
        if (!mounted) return
        if (bundle && bundle.configs && bundle.configs.length > 0) {
          const overrideName = bundle.task_overrides?.project_modify || ''
          const activeName = overrideName || bundle.default_config_name || ''
          const cfg = bundle.configs.find((c) => c.name === activeName) || null
          if (cfg) {
            // Claude Code and Codex each have a dedicated agent dock that
            // streams from their CLI; API-mode configs fall through to the
            // LLM API path.
            if (cfg.mode === 'agent' && cfg.agent === 'claude_code') {
              setMode('claude_code')
            } else if (cfg.mode === 'agent' && cfg.agent === 'codex') {
              setMode('codex')
            } else if (cfg.mode === 'agent' && cfg.agent === 'opencode') {
              // OpenCode shares the Codex dock (same backend router, own engine).
              setMode('opencode')
            } else {
              setMode('llm')
            }
            return
          }
        }
        // No bundle / no configs → fall through to legacy resolution.
        const s = await getSettings()
        if (!mounted) return
        const legacyCfg = (s.task_generation_config as Record<string, { backend?: string }> | undefined)?.['project_modify']
        // effective_generation_mode already resolves the PLATFORM default a
        // new user (no bundle) rides — so the dock opens in agent mode instead
        // of falling back to the legacy 'llm' field. Anything unexpected is
        // treated as the LLM path.
        const eff = s.effective_generation_mode || s.generation_mode || 'llm'
        const effMode = (eff === 'claude_code' || eff === 'codex' || eff === 'opencode' || eff === 'llm') ? eff : 'llm'
        setMode((legacyCfg?.backend as 'llm' | 'claude_code') || effMode)
      } catch {
        if (mounted) setMode('llm')
      }
    }
    resolve()
    return () => {
      mounted = false
    }
    // `project` is a dependency on purpose: the dock is mounted once in the
    // Sidebar and survives navigation, so without it a newly created/opened
    // application would keep the backend resolved when the dock first mounted
    // (e.g. an LLM default changed in another tab, or before this dock
    // existed). Re-resolving per project is one cheap settings fetch.
  }, [reloadKey, project])

  // ModelSettingsModal dispatches this after a save — re-fetch settings so
  // the dock swaps backends if the user just changed generation_mode.
  useEffect(() => {
    const handler = () => setReloadKey((k) => k + 1)
    window.addEventListener('twasta:settings-changed', handler)
    return () => window.removeEventListener('twasta:settings-changed', handler)
  }, [])

  if (mode === null) return null
  // Don't commit to a Claude/Codex CLI dock until we know whether an AutoAgent
  // build plan is still active — otherwise a "continue" lands on the wrong
  // backend before the check resolves. (Skipped when already forced.)
  if (
    activeBuild === null
    && !forceOrchestratorRef.current
    && !runtimeOrchestrator
    && (mode === 'claude_code' || mode === 'codex' || mode === 'opencode')
  ) {
    return null
  }

  if (mode === 'claude_code' && !forceOrchestrator) {
    return (
      <ClaudeAgentDock
        key={`claude-${project}-${reloadKey}`}
        project={project}
        projectTitle={projectTitle}
        onHandoffToOrchestrator={requestOrchestratorHandoff}
      />
    )
  }
  if ((mode === 'codex' || mode === 'opencode') && !forceOrchestrator) {
    return (
      <CodexAgentDock
        key={`${mode}-${project}-${reloadKey}`}
        engine={mode}
        project={project}
        projectTitle={projectTitle}
        onHandoffToOrchestrator={requestOrchestratorHandoff}
      />
    )
  }
  return (
    <AutoAgentDock
      key={`auto-${project}-${reloadKey}`}
      project={project}
      projectTitle={projectTitle}
      handoffMessageRef={handoffMsgRef}
    />
  )
}

// ---------------------------------------------------------------------------
// Shared dock chrome — bar (textarea + send) + slide-up panel container with
// header (project, WS badge, maximize, close). Body content is provided by
// the agent-specific component.
// ---------------------------------------------------------------------------

interface DockShellProps {
  projectTitle: string
  // Project folder name — used to seed up-arrow prompt-history recall.
  project?: string
  wsStatus: ConnectionStatus
  panelOpen: boolean
  setPanelOpen: (v: boolean) => void
  maximized: boolean
  setMaximized: (v: boolean) => void
  isStreaming: boolean
  onSend: (text: string, attachments: Attachment[]) => void | Promise<void>
  onCancel?: () => void
  placeholder?: string
  // Body content for the panel; rendered above the input.
  panelBody: ReactNode
  // Optional plan-switcher slot rendered at the top of the scrollable body
  // (above panelBody). The Ask-AI docks pass <PlansPanel/> here.
  plansSlot?: ReactNode
  // Optional pinned footer rendered between the scrollable body and the input
  // (does NOT scroll). The Auto Agent uses it to keep the object-generation
  // status + Continue controls fixed at the bottom of the chat window.
  panelFooter?: ReactNode
  // Latest one-line stream status shown when the panel is closed but the
  // agent is still working. Lets the user see live progress without keeping
  // the full panel open.
  transientStatus?: string | null
  // Monotonic counter that bumps whenever new content is appended to the
  // panel body (messages, logs, streaming chunks). DockShell uses this to
  // (a) auto-scroll to the bottom only when the user is already there and
  // (b) show the "New messages" indicator otherwise.
  contentSignal?: number
  // True when the conversation has any messages — used so the single-line
  // strip stays visible after the agent completes so the user can re-open
  // the panel to see the response.
  hasHistory?: boolean
  // Wipe the conversation. Renders a "New chat" button in the panel header
  // when provided.
  onNewChat?: () => void
  // True when the agent paused at a review boundary (prototype / end of a
  // phase). Surfaces a Continue affordance in the minimised rail.
  awaitingReview?: boolean
  // Continue with the next phase from a review pause (minimised-rail shortcut).
  onContinue?: () => void
  // How many plans are completed (hidden by default) — drives the "show
  // completed plans" toggle in the header. 0 hides the toggle entirely.
  completedPlanCount?: number
  // Opens the "Continue on phone" QR hand-off. Renders a phone button in the
  // header when provided (only the build dock, which the phone app drives).
  onContinueOnPhone?: () => void
}

function DockShell({
  project,
  wsStatus,
  panelOpen,
  setPanelOpen,
  maximized,
  setMaximized,
  isStreaming,
  onSend,
  onCancel,
  placeholder,
  panelBody,
  plansSlot,
  panelFooter,
  transientStatus,
  contentSignal = 0,
  hasHistory = false,
  onNewChat,
  awaitingReview = false,
  onContinue,
  completedPlanCount = 0,
  onContinueOnPhone,
}: DockShellProps) {
  const showCompletedPlans = useDockUiStore((s) => s.showCompletedPlans)
  const setShowCompletedPlans = useDockUiStore((s) => s.setShowCompletedPlans)
  const toggleCompletedPlans = useCallback(
    () => setShowCompletedPlans(!showCompletedPlans),
    [showCompletedPlans, setShowCompletedPlans],
  )
  const [chatInput, setChatInput] = useState('')
  // The dock imports a Claude Design export (.zip) before the agent's turn.
  const att = useAttachments({ allowDesign: true })
  // Sources attached from the "+" menu (a database connection, a git/zip
  // reference source, an IOFlow connector). Each contributes a machine-readable
  // line to the next message so the build resolves the exact source instead of
  // inferring one from the prose.
  const [sources, setSources] = useState<AttachedSource[]>([])
  const [sourceModalKind, setSourceModalKind] = useState<SourceKind | null>(null)
  // "Build one like this": an existing IOFlow flow/example/connector attached
  // as reference. It rides the ordinary text-attachment path, so nothing
  // downstream needs to know it came from IOFlow.
  const [ioflowRefOpen, setIoflowRefOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Shell-style prompt-history recall (Up/Down arrow). Seeded from the
  // persisted Ask-AI prompt log for this project and grown with each send.
  // newest-first. historyIndex -1 = not navigating; draftRef holds the
  // in-progress input so ArrowDown past the newest restores it.
  const [promptHistory, setPromptHistory] = useState<string[]>([])
  const historyIndexRef = useRef(-1)
  const draftRef = useRef('')
  const scrollRef = useRef<HTMLDivElement>(null)
  // Scroll-position state for the panel body.
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [hasNewBelow, setHasNewBelow] = useState(false)
  const lastSignalRef = useRef(contentSignal)

  // Auto-grow textarea up to TEXTAREA_MAX_HEIGHT, never shrinking below the
  // full height of the bar (TEXTAREA_BASE_HEIGHT).
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const next = Math.min(Math.max(el.scrollHeight, TEXTAREA_BASE_HEIGHT), TEXTAREA_MAX_HEIGHT)
    el.style.height = `${next}px`
  }, [chatInput])

  // Seed prompt-history recall from the persisted prompt log for this project.
  useEffect(() => {
    let cancelled = false
    if (!project) { setPromptHistory([]); return }
    ;(async () => {
      try {
        // userOnly: recall must contain only what the user actually TYPED —
        // never system-generated turns (verify-gate corrective passes,
        // clicked question answers, button-built prompts).
        const rows = await getRecentPrompts({ project, limit: 50, userOnly: true })
        if (cancelled) return
        // Newest-first, de-duplicate consecutive repeats, drop blanks.
        const seen = new Set<string>()
        const texts: string[] = []
        for (const r of rows) {
          const t = (r.prompt_text || '').trim()
          if (!t || seen.has(t)) continue
          seen.add(t)
          texts.push(t)
        }
        setPromptHistory(texts)
      } catch {
        if (!cancelled) setPromptHistory([])
      }
    })()
    return () => { cancelled = true }
  }, [project])

  // One-shot seeded prompt hand-off (e.g. "Replicate from Source Code"
  // registers a reference source, then opens the dock with the analysis
  // prompt prefilled for the user to review and send).
  useEffect(() => {
    if (!project) return
    import('@/api/referenceSources').then(({ takeDockSeedPrompt }) => {
      const seed = takeDockSeedPrompt(project)
      if (seed) {
        setChatInput(seed)
        setPanelOpen(true)
      }
    }).catch(() => { /* hand-off is best-effort */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project])

  // A starter suggestion on the empty dock fills the box with a sentence to
  // finish (it never sends by itself) and puts the caret at the first blank.
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const text = (e as CustomEvent).detail?.text
      if (typeof text !== 'string' || !text) return
      historyIndexRef.current = -1
      setChatInput(text)
      setPanelOpen(true)
      requestAnimationFrame(() => {
        const el = textareaRef.current
        if (!el) return
        el.focus()
        const start = text.indexOf('[')
        const end = start >= 0 ? text.indexOf(']', start) + 1 : text.length
        el.setSelectionRange(start >= 0 ? start : text.length, end > 0 ? end : text.length)
      })
    }
    window.addEventListener(DOCK_PREFILL_EVENT, onPrefill)
    return () => window.removeEventListener(DOCK_PREFILL_EVENT, onPrefill)
  }, [setPanelOpen])

  // Up/Down arrow recall. Only intercept when the caret is at the very start
  // of the input (or it's empty) so normal caret movement in multi-line text
  // is unaffected.
  const recallHistory = useCallback((dir: 'up' | 'down') => {
    if (promptHistory.length === 0) return
    let idx = historyIndexRef.current
    if (dir === 'up') {
      if (idx === -1) { draftRef.current = chatInput; idx = 0 }
      else idx = Math.min(idx + 1, promptHistory.length - 1)
    } else {
      if (idx <= -1) return
      idx = idx - 1
    }
    historyIndexRef.current = idx
    const next = idx === -1 ? draftRef.current : promptHistory[idx]
    setChatInput(next)
    // Place caret at end after the value updates.
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (el) { el.selectionStart = el.selectionEnd = el.value.length }
    })
  }, [promptHistory, chatInput])

  // Escape exits maximize so the user can always get back to the page.
  useEffect(() => {
    if (!maximized) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMaximized(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [maximized, setMaximized])

  // If the user navigates (clicks a TopBar tab, opens a designer, etc.) while
  // the dock is maximized, drop it back to the regular panel so they can
  // actually see the page they switched to. We restore on EITHER a real
  // route change or a workbench tab change — workbench tabs swap the body
  // without touching the URL.
  const location = useLocation()
  const activeTab = useProjectStore((s) => s.activeTab)
  useEffect(() => {
    if (maximized) setMaximized(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, activeTab])

  const handleSend = useCallback(async () => {
    const text = chatInput.trim()
    const attachments = att.attachments
    // Allow image/file-only sends (no text) as long as something is attached.
    if (!text && attachments.length === 0 && sources.length === 0) return
    setChatInput('')
    att.clear()
    // Push onto recall history (newest-first, no consecutive dup) and reset
    // the navigation cursor. Only the user's own words go into history — the
    // source lines are machinery, not something they typed.
    if (text) {
      setPromptHistory((prev) => (prev[0] === text ? prev : [text, ...prev]))
    }
    historyIndexRef.current = -1
    draftRef.current = ''
    // Attached sources lead the message so the build resolves the EXACT source
    // instead of inferring one from the prose.
    const outgoing = sources.length
      ? `${sources.map((s) => s.promptLine).join('\n')}\n${text}`.trim()
      : text
    setSources([])
    await onSend(outgoing, attachments)
  }, [chatInput, att, onSend, sources])

  const toggleMaximized = () => setMaximized(!maximized)
  const minimisePanel = () => setPanelOpen(false)
  const expandPanel = () => setPanelOpen(true)
  const { confirm: shellConfirm, confirmDialog: shellConfirmDialog } = useConfirm()
  const handleNewChat = useCallback(async () => {
    if (!onNewChat || isStreaming) return
    const ok = await shellConfirm({
      title: 'Start a new chat',
      message: 'Start a new chat? This clears the current conversation.',
      confirmLabel: 'Start new chat',
      tone: 'primary',
    })
    if (ok) onNewChat()
  }, [onNewChat, isStreaming, shellConfirm])

  // Scroll handling for the panel body.
  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    setIsAtBottom(atBottom)
    if (atBottom && hasNewBelow) setHasNewBelow(false)
  }

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    setIsAtBottom(true)
    setHasNewBelow(false)
  }, [])

  // When new content arrives: auto-scroll only if the user is already at the
  // bottom. If they've scrolled up to read history, leave them in place and
  // surface a "New messages" pill instead.
  useEffect(() => {
    if (contentSignal === lastSignalRef.current) return
    lastSignalRef.current = contentSignal
    if (!panelOpen) return
    if (isAtBottom) {
      // Defer to next frame so the new content is laid out before scrolling.
      requestAnimationFrame(scrollToBottom)
    } else {
      setHasNewBelow(true)
    }
  }, [contentSignal, isAtBottom, panelOpen, scrollToBottom])

  // When the panel first opens (or remounts), park at the bottom so the
  // newest content is visible.
  useEffect(() => {
    if (panelOpen) requestAnimationFrame(scrollToBottom)
  }, [panelOpen, scrollToBottom])

  return (
    <div
      className="askai-dock"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        // Minimised, the rail continues the white sidebar strip above it.
        background: panelOpen ? 'var(--color-bg)' : 'var(--color-bg-secondary)',
      }}
    >
      {panelOpen && (
        <div
          style={{
            // As a left rail the panel always fills the available column space;
            // the inner scroll viewport (flex:1 + height:0) handles overflow.
            flex: '1 1 0',
            minHeight: 0,
            height: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--color-bg-secondary)',
            borderBottom: '1px solid var(--color-border)',
            position: 'relative',
          }}
        >
          {/* Header: the title with the model/status line under it, New chat,
              and ONE ⋯ menu for everything else (model settings, finished
              plans, full screen, collapse, continue on phone). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.625rem',
              padding: '0.625rem 0.75rem 0.625rem 1.125rem',
              borderBottom: '1px solid var(--color-border)',
              flexShrink: 0,
              background: 'var(--color-bg-secondary)',
              // Positioned so its z-index actually applies — keeps the overflow
              // menu painting above the scroll viewport below it.
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Sparkles size={18} aria-hidden="true" style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, lineHeight: 1.25 }}>
              <span style={{ fontSize: '1.125rem', fontWeight: 600 }}>{DOCK_TITLE}</span>
              {/* Model + live status (dot) — click to change the agent model. */}
              <ModelChip isStreaming={isStreaming} wsStatus={wsStatus} />
            </div>
            {!!onNewChat && (
              <DockIconButton
                label={isStreaming ? 'Stop the agent before starting a new chat' : 'New chat'}
                disabled={isStreaming}
                onClick={handleNewChat}
              >
                <Plus size={18} aria-hidden="true" />
              </DockIconButton>
            )}
            <HeaderOverflowMenu
              maximized={maximized}
              onToggleMax={toggleMaximized}
              onMinimise={minimisePanel}
              showCompletedPlans={showCompletedPlans}
              onToggleCompletedPlans={toggleCompletedPlans}
              completedPlanCount={completedPlanCount}
              onContinueOnPhone={onContinueOnPhone}
            />
          </div>

          {/* Scroll viewport — plain block container with overflow-y:auto.
              The `height: 0 + flex: 1 1 0` combo is the canonical fix for a
              scrollable flex child: it forces the item to grow into the
              available column space rather than expanding to its content's
              natural size (which is what was breaking scroll in maximized
              mode — the inner content was pushing the viewport beyond the
              panel boundary instead of triggering its own overflow). */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              flex: '1 1 0',
              height: 0,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'relative',
            }}
          >
            <div
              style={{
                padding: maximized ? '1.25rem 1.5rem' : '1.125rem 1.125rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.125rem',
                maxWidth: maximized ? 880 : undefined,
                margin: maximized ? '0 auto' : undefined,
                boxSizing: 'border-box',
                width: '100%',
              }}
            >
              {plansSlot}
              {panelBody}
            </div>
          </div>

          {shellConfirmDialog}

          {/* "New messages" pill — appears when the user has scrolled up and
              new content arrives. Click to jump back to the latest. */}
          {hasNewBelow && (
            <button
              onClick={scrollToBottom}
              title="Jump to latest"
              style={{
                position: 'absolute',
                left: '50%',
                bottom: 12,
                transform: 'translateX(-50%)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                background: 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-full)',
                fontSize: '0.9375rem',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                cursor: 'pointer',
                zIndex: 3,
              }}
            >
              <ArrowDown size={12} />
              New messages
            </button>
          )}
        </div>
      )}

      {/* Single-line strip above the bar — visible whenever the panel is
          minimised AND either the agent is streaming OR there's a recent
          conversation. After completion it stays so the user always has a
          way to open the panel and re-read the response. */}
      {/* Minimised vertical rail. Clicking anywhere (except Stop) restores the
          panel. The live agent status reads vertically; a Stop button cancels
          the run, mirroring the maximise/restore controls of the open panel. */}
      {!panelOpen && (
        <div
          onClick={expandPanel}
          title={`Open ${DOCK_TITLE}`}
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.625rem 0',
            cursor: 'pointer',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); expandPanel() }}
            title="Restore agent panel"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              background: 'var(--color-bg-secondary)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <ChevronRight size={15} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setMaximized(true) }}
            title="Maximize agent"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              background: 'var(--color-bg-secondary)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Maximize2 size={14} />
          </button>

          {/* Vertical status — fills the middle, reads top-to-bottom. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              overflow: 'hidden',
            }}
          >
            {awaitingReview ? (
              <Clock size={16} style={{ color: 'var(--color-warning)', flexShrink: 0 }} />
            ) : isStreaming ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)', flexShrink: 0 }} />
            ) : hasHistory ? (
              <CheckCircle2 size={16} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
            ) : (
              <Sparkles size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
            )}
            <span
              title={
                awaitingReview ? (transientStatus || 'Review the plan, then Continue')
                : transientStatus || (isStreaming ? 'Agent is working…' : hasHistory ? 'Response ready' : DOCK_TITLE)
              }
              style={{
                writingMode: 'vertical-rl',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxHeight: '100%',
                fontSize: '1rem',
                color: 'var(--color-text-secondary)',
              }}
            >
              {awaitingReview
                ? (transientStatus ? `Review · ${transientStatus}` : 'Review — tap Continue')
                : isStreaming
                ? (transientStatus || 'Agent is working…')
                : hasHistory
                  ? (transientStatus ? `Done · ${transientStatus}` : 'Response ready')
                  : DOCK_TITLE}
            </span>
          </div>

          {awaitingReview && onContinue ? (
            <button
              className="btn btn-primary"
              onClick={(e) => { e.stopPropagation(); onContinue() }}
              title="Continue — build the next phase"
              aria-label="Continue — build the next phase"
              style={{ width: 32, height: 32, padding: 0, flexShrink: 0 }}
            >
              <PlayCircle size={16} />
            </button>
          ) : isStreaming && onCancel ? (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel() }}
              title="Stop the agent"
              aria-label="Stop the agent"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                background: 'var(--color-secondary-btn)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <Square size={12} fill="currentColor" />
            </button>
          ) : null}
        </div>
      )}

      {/* Pinned footer — the build status (progress + plan + review) stays fixed
          at the bottom of the chat window, above the message box, so it is
          always reachable after the user scrolls through chat. */}
      {panelOpen && panelFooter && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.625rem',
            padding: '0.75rem 1.125rem 0',
            borderTop: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
            flexShrink: 0,
          }}
        >
          {panelFooter}
        </div>
      )}

      {panelOpen && (
      <div
        style={{
          padding: panelFooter ? '0.625rem 1.125rem 1rem' : '0.75rem 1.125rem 1rem',
          borderTop: panelFooter ? undefined : '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
          flexShrink: 0,
        }}
      >
        <input
          ref={att.fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT_WITH_DESIGN}
          onChange={att.onFileInputChange}
          style={{ display: 'none' }}
        />
        {/* ONE message box: attached files/sources, the text, then + on the
            left and Send/Stop on the right — all inside the same border. */}
        <div className="askai-composer">
          <AttachmentChips
            attachments={att.attachments}
            onRemove={att.remove}
            disabled={isStreaming}
          />
          {sources.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
              {sources.map((s, i) => (
                <span
                  key={`${s.kind}-${i}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.25rem 0.625rem',
                    fontSize: '0.9375rem',
                    background: 'var(--color-surface-2)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '999px',
                    color: 'var(--color-text)',
                    maxWidth: '100%',
                  }}
                >
                  <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>
                    {SOURCE_TYPES.find((t) => t.kind === s.kind)?.icon || 'link'}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSources((prev) => prev.filter((_, j) => j !== i))}
                    disabled={isStreaming}
                    title="Remove source"
                    aria-label={`Remove ${s.label}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', border: 'none',
                      background: 'transparent', cursor: 'pointer', padding: 0,
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={chatInput}
            onChange={(e) => {
              // Any manual edit drops out of history navigation so the next
              // ArrowUp starts again from the newest prompt.
              historyIndexRef.current = -1
              setChatInput(e.target.value)
            }}
            placeholder={placeholder || 'Ask for a change…'}
            title="Enter to send · Shift+Enter for a new line · ↑ for recent prompts"
            aria-label={`Message ${DOCK_TITLE}`}
            rows={2}
            autoComplete="off"
            data-form-type="other"
            data-lpignore="true"
            {...att.inputHandlers}
            onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              handleSend()
              return
            }
            // Shell-style prompt recall that still lets you move the caret
            // between lines of a multi-line draft — including soft-wrapped rows
            // (which have no '\n', so line-parsing fails). The caret being at
            // the very START means ArrowUp can't move up any further → recall
            // an earlier prompt; at the very END means ArrowDown can't move
            // down → step forward in history. Anywhere else the arrows just
            // move the caret normally. No active selection.
            const el = e.currentTarget
            const noSelection = el.selectionStart === el.selectionEnd
            if (e.key === 'ArrowUp' && !e.shiftKey && noSelection && el.selectionStart === 0) {
              if (promptHistory.length > 0) {
                e.preventDefault()
                recallHistory('up')
              }
            } else if (e.key === 'ArrowDown' && !e.shiftKey && noSelection && el.selectionStart === el.value.length) {
              if (historyIndexRef.current >= 0) {
                e.preventDefault()
                recallHistory('down')
              }
            }
          }}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: 0,
            fontSize: '1.0625rem',
            lineHeight: 1.5,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--color-text)',
            resize: 'none',
            fontFamily: 'var(--font-primary)',
            minHeight: TEXTAREA_BASE_HEIGHT,
            maxHeight: TEXTAREA_MAX_HEIGHT,
            overflowY: 'auto',
          }}
        />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AddSourceButton
              onAttachFile={att.openFilePicker}
              onAttachIOFlow={() => setIoflowRefOpen(true)}
              onSelectSource={(k) => setSourceModalKind(k)}
              placement="top"
              align="left"
              size="md"
              title="Attach a file or add a data source"
              background="transparent"
              backgroundOpen="var(--color-accent-weak)"
              iconSize={18}
              buttonStyle={{ height: 36, width: 36, minWidth: 36, color: 'var(--color-text)' }}
            />
            <span style={{ flex: 1 }} />
            {isStreaming && onCancel ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                title="Stop the agent"
                style={{
                  height: 36,
                  padding: '0 0.875rem',
                  gap: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                  border: '1px solid var(--color-border)',
                }}
              >
                <Square size={12} fill="currentColor" aria-hidden="true" />
                Stop
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSend}
                disabled={!chatInput.trim() && att.attachments.length === 0
                          && sources.length === 0}
                title="Send (Enter)"
                aria-label="Send"
                style={{ height: 36, width: 36, padding: 0 }}
              >
                <ArrowUp size={18} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* "Add a source" — registers through the same APIs (and the same
          connection editor) the workbench and Analytics tab use. */}
      <AddSourceModal
        open={sourceModalKind !== null}
        kind={sourceModalKind}
        project={project || ''}
        onClose={() => setSourceModalKind(null)}
        onAttach={(s) => setSources((prev) => [...prev, s])}
        onPickSpecFile={att.openFilePicker}
      />

      {/* An existing IOFlow flow / example / connector, attached as reference
          text so the agent can be told "build one like this". */}
      <IOFlowReferencePicker
        project={project || ''}
        open={ioflowRefOpen}
        onClose={() => setIoflowRefOpen(false)}
        onAttach={(text, name) => att.addText(text, name)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// AutoAgent dock — LLM mode. Sends sendMessage with intent_hint='project_change'
// so the orchestrator skips its intent classifier and routes straight to the
// change_agent path.
// ---------------------------------------------------------------------------

function AutoAgentDock({
  project, projectTitle, handoffMessageRef,
}: RouterProps & { handoffMessageRef?: { current: string | null } }) {
  const location = useLocation()
  const visible = isProjectContext(location.pathname)

  const panelOpen = useDockUiStore((s) => s.panelOpen)
  const setPanelOpen = useDockUiStore((s) => s.setPanelOpen)
  const maximized = useDockUiStore((s) => s.maximized)
  const setMaximized = useDockUiStore((s) => s.setMaximized)
  const [sessionReady, setSessionReady] = useState(false)
  const [initializing, setInitializing] = useState(false)
  const { confirm, confirmDialog } = useConfirm()

  const agent = useAutoAgent({ project })
  // "Continue on phone" QR hand-off dialog.
  const [phoneHandoffOpen, setPhoneHandoffOpen] = useState(false)

  // Cross-backend plan list (Claude/Codex plans too) via REST, merged with the
  // AutoAgent WS summaries (which carry live counts for the active session).
  const [restPlans, setRestPlans] = useState<PlanItem[]>([])
  const refreshRestPlans = useCallback(async () => {
    try {
      const list = await listPlans(project)
      setRestPlans(list.map((p) => {
        const todos = (p.todos || []) as Array<{ status?: string }>
        const work = todos.filter((t) => t.status !== undefined)
        return {
          id: p.id, title: p.title, kind: p.kind,
          status: p.status as PlanItem['status'], backend: p.backend,
          updated_at: p.updated_at,
          n_total: work.length,
          n_done: work.filter((t) => t.status === 'completed' || t.status === 'skipped').length,
          depends_on: p.depends_on,
        }
      }))
    } catch { /* registry may be empty */ }
  }, [project])
  useEffect(() => { void refreshRestPlans() }, [refreshRestPlans, agent.agentStatus])

  // Merge: REST is the cross-backend base; AutoAgent WS summaries win for plans
  // owned by the live session (fresher counts/status).
  const mergedPlans: PlanItem[] = (() => {
    const byId = new Map<string, PlanItem>()
    for (const p of restPlans) byId.set(p.id, p)
    for (const p of agent.plans) {
      byId.set(p.id, {
        id: p.id, title: p.title, kind: p.kind,
        status: p.status as PlanItem['status'], backend: 'autoagent',
        n_done: p.n_done, n_total: p.n_total, updated_at: p.updated_at,
        depends_on: p.depends_on,
      })
    }
    return Array.from(byId.values()).sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0))
  })()
  const showCompletedPlans = useDockUiStore((s) => s.showCompletedPlans)
  const completedPlanCount = mergedPlans.filter((p) => p.status === 'completed').length
  // The build-status footer (progress + task list + review controls) is one
  // collapsible widget; collapsed it shows just the progress line + Continue.
  const [footerExpanded, setFooterExpanded] = useState(false)

  const handleResumePlan = useCallback((id: string) => {
    const p = mergedPlans.find((x) => x.id === id)
    if (p && p.backend && p.backend !== 'autoagent') {
      toast(`This is a ${p.backend} plan — switch the dock to ${p.backend} mode to resume it.`)
      return
    }
    agent.resumePlan(id, 'phase')
  }, [mergedPlans, agent])

  // Delete via REST, which purges every store that holds the plan (registry
  // mirror + .autoagent_state.json + any live session). The WS path is used
  // FIRST for an AutoAgent plan so the live session drops it and re-broadcasts
  // immediately — but it is not sufficient on its own (a session that never
  // hydrated the plan answers "Plan not found"), so REST always follows as the
  // authoritative delete. Failures are surfaced: a silent catch here is what
  // made a stuck plan look undeletable.
  const handleDeletePlan = useCallback(async (id: string) => {
    const p = mergedPlans.find((x) => x.id === id)
    if (p && p.backend === 'autoagent' && agent.wsStatus === 'connected') {
      agent.deletePlan(id)
    }
    try {
      await deletePlanApi(project, id)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        // Already gone (the WS delete above got there first) — not an error.
      } else if (status === 409) {
        const ok = await confirm({
          title: 'Plan is still running',
          message: 'This plan is still running. Stop it and delete it anyway?',
          confirmLabel: 'Stop and delete',
        })
        if (ok) {
          try {
            await deletePlanApi(project, id, true)
          } catch {
            toast.error("Couldn't delete the plan. Please try again.")
          }
        }
      } else {
        toast.error("Couldn't delete the plan. Please try again.")
      }
    }
    // The progress footer is driven by the agent's todos, not by the plans
    // list, so it would otherwise keep showing the deleted plan's task card
    // (and its Continue controls) until a page refresh.
    agent.forgetPlanState?.(id)
    void refreshRestPlans()
    agent.refreshPlans?.()
  }, [mergedPlans, agent, project, refreshRestPlans, confirm])

  const wsStatusRef = useRef(agent.wsStatus)
  useEffect(() => {
    wsStatusRef.current = agent.wsStatus
  }, [agent.wsStatus])

  // When the agent finishes a project_change run (running → paused/completed)
  // and there are completed change_* todos, broadcast so any open previews
  // (TransactionPreviewPane, runtime iframe) can refresh their metadata.
  const prevStatusRef = useRef(agent.agentStatus)
  // Keep the plan switcher counts/statuses fresh as a run progresses or settles.
  useEffect(() => {
    if (agent.wsStatus === 'connected') agent.refreshPlans()
  }, [agent.agentStatus, agent.wsStatus, agent.refreshPlans])
  useEffect(() => {
    const prev = prevStatusRef.current
    const now = agent.agentStatus
    prevStatusRef.current = now
    if (prev === 'running' && (now === 'paused' || now === 'completed')) {
      const hadChanges = agent.todos.some(
        (t) => t.id.startsWith('change_') && t.status === 'completed',
      )
      if (hadChanges) {
        window.dispatchEvent(
          new CustomEvent('twasta:agent-files-changed', {
            detail: { project, files: [] },
          }),
        )
      }
    }
  }, [agent.agentStatus, agent.todos, project])

  // Auto-scroll is now handled inside DockShell — it preserves the user's
  // scroll position and only follows the bottom when they're already there.

  // Surface agent questions that need an answer — they otherwise hide.
  useEffect(() => {
    if (visible && !panelOpen && agent.question) {
      setPanelOpen(true)
    }
  }, [visible, panelOpen, agent.question])

  // Auto-start the first-generation pipeline when arriving from "Build it" on
  // the Builder Hub. The pending description is consumed exactly once;
  // agent.start() creates the session, opens the WS and fires the orchestrator.
  // We open the panel so the user immediately sees the task list + progress.
  const didAutoStartRef = useRef(false)
  useEffect(() => {
    if (!visible || didAutoStartRef.current) return
    const desc = getPendingBuild(project)
    if (!desc) return
    didAutoStartRef.current = true
    // Read the staged files BEFORE clearing — a spec attached on the hub has
    // to reach the very first FRS pass.
    const pendingAtts = getPendingAttachments(project)
    autoBuildRef.current = !getPendingReviewFirst(project)
    clearPendingBuild()
    setPanelOpen(true)
    ;(async () => {
      try {
        // The Builder hub's "Build" is the agentic, multi-agent path — the
        // orchestrator decides the steps (profiling a referenced source,
        // spec, models) rather than the deterministic step pipeline.
        await agent.start(desc, undefined, 'build', pendingAtts)
        // Mark the session ready so later change requests reuse it instead of
        // spinning up a second WebSocket connection.
        setSessionReady(true)
      } catch {
        /* failure is surfaced via agent.error / the log stream */
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, project])

  // When the dock is shown for a project that has a resumable AutoAgent plan
  // (e.g. a paused "Build application" plan), connect on mount and load that
  // plan's live state so its task tracker + Continue controls surface. Without
  // this the socket stays closed, `agent.todos` stays empty, and the tracker /
  // Continue buttons (all derived from todos) never render — even though the
  // PLANS list still shows via the separate REST call. This never *runs* a
  // plan: only get_state + select_plan are used, never resume_plan.
  const didConnectOnMountRef = useRef(false)
  useEffect(() => {
    if (!visible || didConnectOnMountRef.current) return
    // Don't collide with the Builder-handoff autostart or a runtime handoff —
    // those connect (and start) on their own.
    if (didAutoStartRef.current || getPendingBuild(project) !== null) return
    if (handoffMessageRef?.current) return
    if (sessionReady || initializing) return
    // Stay lazy unless there is a resumable AutoAgent plan whose controls we
    // must surface.
    const resumable = restPlans.filter(
      (p) => p.backend === 'autoagent' && p.status !== 'completed' && (p.n_total ?? 0) > 0,
    )
    if (resumable.length === 0) return
    didConnectOnMountRef.current = true
    ;(async () => {
      if (!(await ensureSession())) return         // initSession → connect → get_state
      if (!(await waitForConnected())) return
      // get_state loaded the active plan on WS open. If that isn't the resumable
      // build plan, select it (view-only — does NOT run) so its tracker +
      // Continue controls show.
      if (!resumable.some((p) => p.id === agent.activePlanId)) {
        const isBuild = (k?: string) => k === 'build' || k === 'sample_data'
        const target = [...resumable].sort((a, b) =>
          (isBuild(b.kind) ? 1 : 0) - (isBuild(a.kind) ? 1 : 0)   // build-kind first
          || (b.updated_at ?? 0) - (a.updated_at ?? 0),           // then most recent
        )[0]
        if (target) agent.selectPlan(target.id)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, restPlans])

  const initPromiseRef = useRef<Promise<boolean> | null>(null)
  const ensureSession = useCallback(async () => {
    // A ready session is only trustworthy while its socket is alive (or
    // actively reconnecting): a backend restart or an exhausted reconnect
    // leaves sessionReady=true over a dead connection, and every send after
    // that would be dropped. Re-initialize instead.
    const wsAlive =
      wsStatusRef.current === 'connected' || wsStatusRef.current === 'connecting'
    if (sessionReady && wsAlive) return true
    // Share one in-flight init: a message typed during the mount-time connect
    // must wait for it, not get dropped because "initializing" was true.
    if (initPromiseRef.current) return initPromiseRef.current
    const run = (async () => {
      setInitializing(true)
      try {
        const result = await agent.initSession()
        const ok = !!result
        setSessionReady(ok)
        return ok
      } catch {
        setSessionReady(false)
        return false
      } finally {
        setInitializing(false)
        initPromiseRef.current = null
      }
    })()
    initPromiseRef.current = run
    return run
  }, [agent, sessionReady])

  const waitForConnected = useCallback(async (timeoutMs = 8000) => {
    if (wsStatusRef.current === 'connected') return true
    const start = Date.now()
    return new Promise<boolean>((resolve) => {
      const tick = () => {
        if (wsStatusRef.current === 'connected') return resolve(true)
        if (wsStatusRef.current === 'error') return resolve(false)
        if (Date.now() - start > timeoutMs) return resolve(false)
        setTimeout(tick, 100)
      }
      tick()
    })
  }, [])

  // Clicking a plan row must WORK from a cold dock. The dock stays lazy until
  // it sees a resumable plan with todos, so selecting any other plan (or one
  // clicked after the socket dropped) used to fail with "the agent connection
  // is down". Open the connection first, then select.
  const handleSelectPlan = useCallback(async (id: string) => {
    if (agent.wsStatus !== 'connected') {
      if (!(await ensureSession())) return
      if (!(await waitForConnected())) return
    }
    agent.selectPlan(id)
  }, [agent, ensureSession, waitForConnected])

  // Hand-off from the phone ("Continue in browser" opens
  // /builder/<project>?plan=<id>): open the dock on that plan once.
  const handoffPlanDoneRef = useRef<string | null>(null)
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('plan')
    if (!wanted || handoffPlanDoneRef.current === wanted) return
    handoffPlanDoneRef.current = wanted
    setPanelOpen(true)
    if (agent.activePlanId !== wanted) void handleSelectPlan(wanted)
  }, [location.search, agent.activePlanId, handleSelectPlan, setPanelOpen])

  const handleSend = useCallback(async (text: string, attachments: Attachment[]) => {
    // (Re)establish the session/socket if needed, but NEVER swallow the
    // message on failure: sendMessage echoes the typed text into the
    // transcript and reports an undelivered send itself, so it runs
    // unconditionally — the user always sees what they typed and, if the
    // connection is dead, an explicit error saying so.
    await ensureSession()
    await waitForConnected()
    // intent_hint='project_change' only says the message was typed in the
    // change dock — the backend's AI router still reads it and decides.
    // The open object lets the agent resolve "this" to the screen on view.
    const openObject = useBuilderContextStore.getState().openObject
    agent.sendMessage(
      text, undefined, 'project_change', undefined, openObject,
      attachments.map(toWire),
    )
  }, [ensureSession, waitForConnected, agent])

  // Builder Mode "Create" hands an ungenerated object off to the orchestrator
  // with its spec, so generation runs in this dock (with the task tracker)
  // rather than via a direct API call.
  useEffect(() => {
    const onCreateObject = (e: Event) => {
      const d = (e as CustomEvent).detail as CreateObjectDetail | undefined
      if (!d || d.project !== project || !d.objName) return
      const label = OBJ_TYPE_NOUN[d.objType] || 'object'
      const content =
        `Generate the "${d.title || d.objName}" ${label} `
        + `(object name: ${d.objName}, type: ${d.objType}) using this specification:\n\n`
        + `${d.functionalSpecs || d.description || ''}`
      setPanelOpen(true)
      ;(async () => {
        // Best-effort connect; sendMessage surfaces a delivery failure itself.
        await ensureSession()
        await waitForConnected()
        agent.sendMessage(content, undefined, 'project_change')
      })()
    }
    window.addEventListener('twasta:agent-create-object', onCreateObject as EventListener)
    return () => window.removeEventListener('twasta:agent-create-object', onCreateObject as EventListener)
  }, [project, ensureSession, waitForConnected, agent])

  // A free-form task handed off from elsewhere in the app (e.g. the Database
  // Designer kicking off a database-dialect migration, Testing "Fix with AI").
  // Opens the dock and sends the prompt as a HANDED-OFF task: the orchestrator
  // runs it as its own change plan and never reads its wording as a build /
  // continue request (a test-fix brief's "all pending approvals" did).
  useEffect(() => {
    const onRunTask = (e: Event) => {
      const d = (e as CustomEvent).detail as { project?: string; prompt?: string } | undefined
      if (!d || d.project !== project || !d.prompt) return
      setPanelOpen(true)
      ;(async () => {
        // Best-effort connect; sendMessage surfaces a delivery failure itself.
        await ensureSession()
        await waitForConnected()
        agent.sendMessage(d.prompt as string, undefined, 'task')
      })()
    }
    window.addEventListener('twasta:agent-run-task', onRunTask as EventListener)
    return () => window.removeEventListener('twasta:agent-run-task', onRunTask as EventListener)
  }, [project, ensureSession, waitForConnected, agent])

  // "Unit Test" button in LLM mode: there are no agent tools / sub-agents on
  // this path, so we can't run the data-first agentic loop. Fall back to a
  // one-shot deterministic test and toast the PASS/FAIL summary. (The full
  // agentic experience requires the Claude Code or Codex CLI agent.)
  useEffect(() => {
    const onRunUnitTest = (e: Event) => {
      const d = (e as CustomEvent).detail as RunUnitTestDetail | undefined
      if (!d || d.project !== project || !d.objName) return
      ;(async () => {
        const t = toast.loading(`Unit testing "${d.title || d.objName}"…`)
        try {
          const report = await runUnitTestOnce(d.project, d.objName, d.objType || 'T')
          if (report.ok) {
            toast.success(
              `Unit test PASSED for "${d.title || d.objName}" `
              + `(${report.passed ?? 0} step(s)).`, { id: t },
            )
          } else {
            const ff = report.first_failure
            toast.error(
              `Unit test FAILED at ${ff?.step || 'a step'}: `
              + `${ff?.message || ff?.code || 'see details'}. `
              + `Switch to the Claude Code or Codex agent to auto-fix test data.`,
              { id: t, duration: 8000 },
            )
          }
        } catch (err) {
          toast.error(
            `Unit test could not run: ${(err as Error)?.message || 'unknown error'}`,
            { id: t },
          )
        }
      })()
    }
    window.addEventListener('twasta:agent-run-unit-test', onRunUnitTest as EventListener)
    return () => window.removeEventListener('twasta:agent-run-unit-test', onRunUnitTest as EventListener)
  }, [project])

  // A bulk-build request handed off from the free-form Claude/Codex dock (the
  // user typed "create all the missing objects" there). Send it through the
  // orchestrator so it runs the plan → per-object-review pipeline and surfaces
  // the phase tracker — consumed exactly once.
  const didHandoffRef = useRef(false)
  useEffect(() => {
    if (!visible || didHandoffRef.current) return
    const msg = handoffMessageRef?.current
    if (!msg) return
    didHandoffRef.current = true
    if (handoffMessageRef) handoffMessageRef.current = null
    setPanelOpen(true)
    ;(async () => {
      // Best-effort connect; sendMessage surfaces a delivery failure itself.
      await ensureSession()
      await waitForConnected()
      agent.sendMessage(msg, undefined, 'project_change')
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, handoffMessageRef, ensureSession, waitForConnected, agent])

  // How long the current request has been running — counted from the user's
  // latest message. (A hook, so it sits above the early return.)
  const lastUserReply = [...agent.replies].reverse().find((r) => r.role === 'user')
  const workingElapsed = useElapsed(lastUserReply?.timestamp ?? null, agent.agentStatus === 'running')

  // Split logs: errors/warnings persist as permanent messages; info-level
  // logs are transient — only the latest one is shown as a status line at
  // the bottom so the chat doesn't get spammed by progress chatter.
  const persistentLogs = agent.logs.filter((l) => l.level !== 'info')
  const latestInfoLog = [...agent.logs].reverse().find((l) => l.level === 'info') || null
  // A dropped send (or a dead socket) can leave the status stuck on 'running'
  // with no live stream behind it — never replay an old log line under a
  // spinner as if it were current activity.
  const infoLogIsFresh =
    latestInfoLog !== null
    && Date.now() - latestInfoLog.timestamp.getTime() < 120_000

  const messages = [
    ...persistentLogs.map((l) => ({ kind: 'log' as const, item: l })),
    ...agent.replies.map((r) => ({ kind: 'reply' as const, item: r })),
    ...agent.turnUsages.map((u) => ({ kind: 'usage' as const, item: u })),
  ].sort((a, b) => a.item.timestamp.getTime() - b.item.timestamp.getTime())

  const runningTodo = agent.todos.find((t) => t.status === 'running')
  const completedCount = agent.todos.filter((t) => t.status === 'completed').length
  const totalCount = agent.todos.filter((t) => t.status !== 'skipped').length
  // The plan still has objects left to build. Surface the Continue / Build task
  // by task / Complete-everything controls whenever that's true AND the agent
  // isn't actively running — so a side change (which may leave the status as
  // 'paused' rather than 'awaiting_review') never strands the user without a
  // way back into the plan.
  const planHasPending = agent.todos.some((t) => t.step === 'object' && t.status === 'pending')
  const canContinue = agent.awaitingReview || (planHasPending && agent.agentStatus !== 'running')

  // Publish this dock's build controls to the Workbench (store/dockBridgeStore):
  // the Next step card's Continue / Build the rest / Pause call THESE handlers,
  // so the card and the Review card below are one action.
  const publishBridge = useDockBridgeStore((s) => s.publish)
  const clearBridge = useDockBridgeStore((s) => s.clear)
  const bridgeActions = useMemo(() => ({
    continueNextPhase: agent.continueNextPhase,
    completeEverything: agent.completeEverything,
    pause: agent.pause,
  }), [agent.continueNextPhase, agent.completeEverything, agent.pause])
  useEffect(() => {
    publishBridge({
      project, agentStatus: agent.agentStatus, canContinue,
      hasQuestion: agent.question !== null, actions: bridgeActions,
    })
  }, [project, agent.agentStatus, canContinue, agent.question, bridgeActions, publishBridge])
  useEffect(() => () => clearBridge(project), [project, clearBridge])

  // "Show me the plan before anything is built" (Builder hub, ON by default).
  // The orchestrator always pauses after the structure; with the box cleared
  // the first such pause — nothing built yet — carries straight on, which is
  // the build-without-reading path the plan describes.
  const autoBuildRef = useRef(false)
  useEffect(() => {
    if (!autoBuildRef.current || !agent.awaitingReview) return
    const built = agent.todos.some((t) => t.step === 'object' && t.status === 'completed')
    autoBuildRef.current = false
    if (!built) agent.completeEverything()
  }, [agent.awaitingReview, agent.todos, agent])

  // After the last hook: a route outside the project context (e.g.
  // /j2ee-deploy) hides the dock, and returning above any hook changes the
  // hook count between renders (React #300 — a blank screen).
  if (!visible) return null
  // An adhoc request ("do something else") runs OVER a paused build: the
  // session status flips to 'running', but no build task is actually executing
  // (the running work is surfaced in the chat, not here). Without this, the
  // footer header would spin "Working…" over the dormant pending build tasks —
  // reading as if the build resumed. Drive the footer's running visuals off an
  // actually-running task instead, and show "Paused" otherwise.
  const adhocOverPausedBuild =
    agent.agentStatus === 'running' && planHasPending && !runningTodo
  const showRunning = !!runningTodo || (agent.agentStatus === 'running' && !adhocOverPausedBuild)
  const showPaused = adhocOverPausedBuild || (!showRunning && agent.agentStatus === 'paused')
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
  const showProgress =
    agent.todos.length > 0 ||
    agent.agentStatus === 'running' ||
    !!agent.currentTask ||
    !!agent.streamingStepOutput
  const hasActivity =
    messages.length > 0 ||
    agent.question !== null ||
    !!agent.streamingStepOutput ||
    showProgress

  // Status-priority order for the object list: the one being processed first,
  // then what's still upcoming, then the finished/skipped ones at the bottom.
  // A stable sort keeps the original build order within each status group.
  const TODO_STATUS_ORDER: Record<string, number> = {
    running: 0, error: 1, pending: 2, completed: 3, skipped: 4,
  }
  const sortedTodos = [...agent.todos].sort(
    (a, b) => (TODO_STATUS_ORDER[a.status] ?? 9) - (TODO_STATUS_ORDER[b.status] ?? 9)
  )

  // The build status lives in a PINNED FOOTER (rendered by DockShell between the
  // scrollable chat and the message box): what the plan is doing, "n of m
  // done", a progress bar, View plan, and a Continue shortcut while it waits
  // for review. The full review choices sit at the end of the conversation.
  const hasFooter = agent.todos.length > 0 || canContinue
  const footerTone: 'running' | 'paused' | 'done' | 'error' =
    agent.agentStatus === 'error' ? 'error'
      : agent.agentStatus === 'completed' ? 'done'
      : (canContinue || showPaused) ? 'paused'
      : 'running'
  const footerTitle =
    (showRunning && runningTodo?.label)
    || (canContinue ? 'Paused for review'
      : agent.agentStatus === 'completed' ? 'All done'
      : showPaused ? 'Paused'
      : agent.agentStatus === 'error' ? 'Stopped with an error'
      : showRunning ? 'Working on the plan'
      : 'Plan')
  const statusFooter = (
    <PlanProgressCard
      title={footerTitle}
      subtitle={totalCount > 0 ? `${completedCount} of ${totalCount} done` : undefined}
      progress={progress}
      tone={footerTone}
      open={footerExpanded}
      onToggle={() => setFooterExpanded((v) => !v)}
      action={canContinue ? (
        <button
          type="button"
          className="btn btn-primary"
          onClick={agent.continueNextPhase}
          title="Build the next phase, then pause again"
          style={{ height: 32, padding: '0 0.75rem', fontSize: '0.9375rem', fontWeight: 600, flexShrink: 0 }}
        >
          Continue
        </button>
      ) : undefined}
    >
      {agent.todos.length > 0 ? (
        <TaskList
          tasks={sortedTodos.map((t) => ({
            id: t.id,
            label: t.label,
            status: t.status,
            error: t.error,
            phaseId: t.phase_id,
            reviewIters: t.review_iters,
            stage: t.stage,
            objType: t.obj_type,
            step: t.step,
          }))}
          phases={agent.phases?.map((p) => ({ id: p.id, title: p.title }))}
        />
      ) : undefined}
    </PlanProgressCard>
  )

  // ── The conversation, read as TURNS ──────────────────────────────────────
  // Each user message starts a turn. Inside one: the agent's replies and any
  // warning/error stay visible; success milestones fold into the turn's "Show
  // steps"; the per-turn usage line becomes the quiet "Took … · Details"
  // footer. While the last turn runs, ONE working card replaces the old live
  // status line + raw "Agent output" box.
  const turns = groupTurns(
    messages,
    (m) => m.kind === 'reply' && (m.item as typeof agent.replies[0]).role === 'user',
  )
  const isRunning = agent.agentStatus === 'running'
  // The run is stopped on a question to the user: show the question, not a
  // "Working on it…" spinner that never moves.
  const waitingOnUser = agent.question !== null
  const liveInfo = infoLogIsFresh ? latestInfoLog?.message || null : null
  const workingLabel = runningTodo?.label || liveInfo || 'Working on it…'
  const workingDetail = runningTodo ? liveInfo : null

  const renderTurn = (turn: typeof messages, isLast: boolean, key: number) => {
    const nodes: ReactNode[] = []
    const steps: DockStep[] = []
    let usage: TurnUsage | null = null
    for (const m of turn) {
      if (m.kind === 'reply') {
        const r = m.item as typeof agent.replies[0]
        if (r.role === 'user') {
          nodes.push(<UserBubble key={`r-${r.id}`}>{r.content}</UserBubble>)
        } else if (r.ask) {
          // A question the agent asked. The open one is the card below the
          // conversation — don't show it twice.
          if (agent.question?.question_id === r.ask.question_id) continue
          nodes.push(<AskRecord key={`r-${r.id}`} text={r.content} state={r.ask.state} />)
        } else {
          nodes.push(<AssistantText key={`r-${r.id}`} text={r.content} />)
          if (!usage && (r.durationMs || r.inputTokens || r.outputTokens)) {
            usage = {
              elapsedS: r.durationMs ? r.durationMs / 1000 : null,
              inputTokens: r.inputTokens, outputTokens: r.outputTokens, costUsd: r.costUsd,
            }
          }
        }
      } else if (m.kind === 'usage') {
        const u = m.item as typeof agent.turnUsages[0]
        usage = {
          elapsedS: u.durationMs / 1000,
          inputTokens: u.inputTokens, outputTokens: u.outputTokens,
          costUsd: u.costUsd, calls: u.calls,
        }
      } else {
        const l = m.item as typeof agent.logs[0]
        if (l.level === 'success') {
          steps.push({ text: l.message, level: 'success', elapsedS: l.elapsedS })
        } else {
          nodes.push(
            <DockAlert key={`l-${l.id}`} level={l.level === 'error' ? 'error' : 'warning'}>
              {l.message}
            </DockAlert>,
          )
        }
      }
    }
    const working = isLast && isRunning && !waitingOnUser
    return (
      <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {nodes}
        {working ? (
          <WorkingCard
            label={workingLabel}
            detail={workingDetail}
            elapsedS={workingElapsed}
            steps={dedupeSteps(steps)}
            liveOutput={agent.activeChangeStep ? agent.streamingStepOutput : null}
          />
        ) : (
          <TurnFooter usage={usage} steps={dedupeSteps(steps)} />
        )}
      </div>
    )
  }

  const body = (
    <>
      {!hasActivity && !initializing && <EmptyHint />}

      {initializing && !hasActivity && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            flex: 1,
            color: 'var(--color-text-secondary)',
            fontSize: '1.0625rem',
          }}
        >
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          Connecting to the agent…
        </div>
      )}

      {turns.map((turn, i) => renderTurn(turn, i === turns.length - 1, i))}

      {/* A run with nothing in the conversation yet (e.g. resumed plan). */}
      {isRunning && !waitingOnUser && turns.length === 0 && (
        <WorkingCard
          label={workingLabel}
          detail={workingDetail}
          steps={[]}
          liveOutput={agent.activeChangeStep ? agent.streamingStepOutput : null}
        />
      )}

      {/* Dead-connection banner: whenever the socket is down (backend restart,
          network drop, rejected session), say so explicitly instead of letting
          the dock look alive while every send would be dropped. */}
      {(agent.wsStatus === 'error' || agent.error) && (
        <DockAlert level="error">
          {agent.error
            || 'Lost the connection to the agent. Reconnecting in the background — '
               + 'anything you send before it reconnects will not be delivered.'}
        </DockAlert>
      )}

      {agent.question && <WaitingNote parked={!!agent.question.parked} askedAt={agent.question.asked_at} />}

      {agent.question && agent.question.scope && (
        <ScopeConfirmation
          proposal={agent.question.scope}
          compact
          confirmLabel="Confirm & build"
          onConfirm={(decisions) =>
            agent.answerScopeQuestion(agent.question!.question_id, decisions)}
          onSkip={() => agent.answerQuestion(agent.question!.question_id, '')}
        />
      )}

      {agent.question && agent.question.table_picker && (
        <SourceTablePicker
          payload={agent.question.table_picker}
          question={agent.question.question}
          compact
          onConfirm={(tables, includeRelated) =>
            agent.answerTablePicker(
              agent.question!.question_id, tables, includeRelated)}
          onSkip={() => agent.answerTablePicker(agent.question!.question_id, [])}
        />
      )}

      {agent.question && !agent.question.scope && !agent.question.table_picker && (
        <AgentAskBubble
          question={agent.question.question}
          options={agent.question.options}
          onAnswer={(ans, connectionId) =>
            agent.answerQuestion(agent.question!.question_id, ans, connectionId)}
          connectionForm={!!agent.question.connection_form}
          project={project}
        />
      )}

      {/* Review pause — ONE primary action; the rest are secondary. */}
      {canContinue && !isRunning && (
        <ReviewCard
          onContinue={agent.continueNextPhase}
          onBuildRest={agent.completeEverything}
          onOneByOne={agent.buildTaskByTask}
        />
      )}
      {confirmDialog}
    </>
  )

  // Monotonic-ish signal for DockShell's auto-scroll: bumps when any new
  // content arrives. Hashing string length keeps it cheap.
  const contentSignal =
    agent.replies.length * 100 +
    agent.logs.length +
    (agent.streamingStepOutput?.length || 0) +
    agent.todos.filter((t) => t.status === 'completed').length +
    // A pending clarifying question must scroll into view so the user sees
    // the agent is waiting for input.
    (agent.question ? 51 : 0)

  // One-line status text for the collapsed streaming strip — prefers the
  // currently running todo label, then the agent's live step output's last
  // line, then the latest info log message.
  const transientStatus =
    (agent.question ? 'Waiting for your answer' : null)
    || runningTodo?.label
    || (agent.streamingStepOutput
        ? agent.streamingStepOutput.split('\n').filter(Boolean).pop()
        : null)
    || (infoLogIsFresh ? latestInfoLog?.message : null)
    || (agent.agentStatus === 'running' ? 'Working…' : null)

  const hasHistory = agent.replies.length > 0 || agent.logs.length > 0 || agent.todos.length > 0

  return (
    <>
    <DockShell
      projectTitle={projectTitle}
      project={project}
      wsStatus={agent.wsStatus as ConnectionStatus}
      panelOpen={panelOpen}
      setPanelOpen={setPanelOpen}
      maximized={maximized}
      setMaximized={setMaximized}
      isStreaming={agent.agentStatus === 'running'}
      onSend={handleSend}
      onCancel={agent.cancel}
      panelBody={body}
      plansSlot={
        <PlansPanel
          plans={mergedPlans}
          activePlanId={agent.activePlanId}
          busy={agent.agentStatus === 'running'}
          showCompleted={showCompletedPlans}
          onSelect={handleSelectPlan}
          onResume={handleResumePlan}
          onPause={agent.pausePlan}
          onDelete={handleDeletePlan}
        />
      }
      panelFooter={hasFooter ? statusFooter : undefined}
      transientStatus={transientStatus}
      contentSignal={contentSignal}
      hasHistory={hasHistory}
      onNewChat={agent.clearLogs}
      awaitingReview={canContinue}
      onContinue={agent.continueNextPhase}
      completedPlanCount={completedPlanCount}
      onContinueOnPhone={() => setPhoneHandoffOpen(true)}
    />
    {phoneHandoffOpen && (
      <ContinueOnPhoneModal
        open={phoneHandoffOpen}
        onClose={() => setPhoneHandoffOpen(false)}
        project={project}
        planId={agent.activePlanId}
      />
    )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Claude Agent dock — claude_code mode. Each user message starts a fresh
// project_modify task; the agent has full file tools and can edit anything
// in the project directory.
// ---------------------------------------------------------------------------

function ClaudeAgentDock({
  project, projectTitle, onHandoffToOrchestrator,
}: RouterProps & { onHandoffToOrchestrator?: (msg: string) => void }) {
  const location = useLocation()
  const visible = isProjectContext(location.pathname)

  const panelOpen = useDockUiStore((s) => s.panelOpen)
  const setPanelOpen = useDockUiStore((s) => s.setPanelOpen)
  const maximized = useDockUiStore((s) => s.maximized)
  const setMaximized = useDockUiStore((s) => s.setMaximized)
  const [completedPlanCount, setCompletedPlanCount] = useState(0)

  const agent = useClaudeAgent({ project })
  // The TodoWrite checklist bar starts collapsed (status + count + progress).
  const [checklistOpen, setChecklistOpen] = useState(false)
  // "Continue on phone" QR hand-off dialog (the phone app runs this dock too).
  const [phoneHandoffOpen, setPhoneHandoffOpen] = useState(false)

  // Broadcast file-change after Claude Agent finishes a task, so any open
  // previews (TransactionPreviewPane, runtime iframe, Builder Mode preview)
  // refresh their metadata.
  // Fire whether or not `changedFiles` is populated — the backend doesn't
  // always report touched files in the `complete` event, but the agent
  // may still have edited project files. A spurious refresh is cheap;
  // a missed one leaves the user staring at stale UI.
  const prevStreamingRef = useRef(false)
  useEffect(() => {
    if (prevStreamingRef.current && !agent.isStreaming) {
      window.dispatchEvent(
        new CustomEvent('twasta:agent-files-changed', {
          detail: {
            project,
            files: agent.changedFiles.map((f) => f.path),
            // Deleted paths are carried SEPARATELY: a surface showing a
            // deleted object must evict it, not refetch it (a refetch 404s and
            // leaves the stale object on screen). See useAgentRefresh.
            deleted: agent.changedFiles.filter((f) => f.deleted).map((f) => f.path),
          },
        }),
      )
    }
    prevStreamingRef.current = agent.isStreaming
  }, [agent.isStreaming, agent.changedFiles, project])

  // Auto-scroll is handled inside DockShell — it preserves the user's
  // scroll position and only follows the bottom when they're already there.

  // Permission requests and clarifying questions must surface the panel —
  // they're blocking and the user has to respond.
  useEffect(() => {
    if (
      visible &&
      !panelOpen &&
      agent.messages.some(
        (m) =>
          (m.role === 'permission' && !m.permissionDecision) ||
          (m.role === 'ask' && !m.askAnswered),
      )
    ) {
      setPanelOpen(true)
    }
  }, [visible, panelOpen, agent.messages])

  const handleSend = useCallback(async (
    text: string,
    attachments: Attachment[],
    // internal: the text was NOT typed by the user (clicked question answer,
    // Unit-Test button prompt) — logged for cost but kept out of ↑ recall.
    // handedOff: a ready brief from another screen (Testing "Fix with AI", a
    // designer's hand-off) — it runs as written, never re-routed by its wording.
    opts?: { internal?: boolean; handedOff?: boolean },
  ) => {
    // Generation (build all / missing / remaining objects) always runs through
    // the orchestrator (plan → per-object review), never this free-form change
    // agent which would author every object inline with no plan/review. Not
    // for a handed-off brief: a test-fix brief's "all pending approvals" /
    // "never create …" read as a bulk build and started building objects.
    if (onHandoffToOrchestrator && !opts?.handedOff && isBuildAllRequest(text)) {
      onHandoffToOrchestrator(text)
      return
    }
    const chat_history = agent.messages
      .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'ask')
      // Map an `ask` turn to an assistant turn carrying the question text, so a
      // re-run sees what the agent asked and why this answer is the reply.
      .map((m) => ({
        role: m.role === 'ask' ? 'assistant' : m.role,
        content: m.role === 'ask' ? (m.ask?.question || m.content) : m.content,
      }))
    // Pass the open object so the agent resolves "this" to the screen on view.
    const openObject = useBuilderContextStore.getState().openObject
    await agent.startTask('project_modify', {
      user_input: text,
      description: text,
      project_name: projectTitle,
      chat_history,
      ...(opts?.internal ? { _internal_prompt: true } : {}),
      ...(openObject ? { current_object: openObject } : {}),
      ...(attachments.length ? { attachments: attachments.map(toWire) } : {}),
    })
  }, [agent, projectTitle, onHandoffToOrchestrator])

  // Builder Mode "Create" hands an ungenerated object off to the agent. Route
  // it straight to the specific generation sub-agent (model / visual / page)
  // with the object's spec — all AI work stays in this dock.
  useEffect(() => {
    const onCreateObject = (e: Event) => {
      const d = (e as CustomEvent).detail as CreateObjectDetail | undefined
      if (!d || d.project !== project || !d.objName || !d.objType) return
      const taskType = d.objType === 'V' ? 'visual' : d.objType === 'S' ? 'page' : 'model'
      setPanelOpen(true)
      agent.startTask(taskType, {
        obj_name: d.objName,
        obj_type: d.objType,
        description: d.description || d.functionalSpecs || d.title || d.objName,
        functional_specs: d.functionalSpecs || '',
        project_folder: project,
        project_name: projectTitle,
        ...(d.designReferenceUrl ? { design_reference_url: d.designReferenceUrl } : {}),
      })
    }
    window.addEventListener('twasta:agent-create-object', onCreateObject as EventListener)
    return () => window.removeEventListener('twasta:agent-create-object', onCreateObject as EventListener)
  }, [project, projectTitle, agent])

  // The Transaction Designer's "Unit Test" button routes here — the agent
  // delegates to the unit-test-runner sub-agent (Claude) or runs the same
  // data-first protocol inline (Codex), asking before any model change. Reuses
  // handleSend so chat history / open object are included.
  useEffect(() => {
    const onRunUnitTest = (e: Event) => {
      const d = (e as CustomEvent).detail as RunUnitTestDetail | undefined
      if (!d || d.project !== project || !d.objName) return
      setPanelOpen(true)
      handleSend(buildUnitTestPrompt(d), [], { internal: true })
    }
    window.addEventListener('twasta:agent-run-unit-test', onRunUnitTest as EventListener)
    return () => window.removeEventListener('twasta:agent-run-unit-test', onRunUnitTest as EventListener)
  }, [project, handleSend, setPanelOpen])

  // A designer handing a ready brief to the agent (e.g. the Playbook
  // designer's "Build in Ask-AI" for an IOFlow flow it cannot write itself).
  // `twasta:agent-run-task` (Testing "Fix with AI", the Database Designer's
  // dialect migration) is the same hand-off; the AutoAgent dock runs it as a
  // plan, this dock as a turn — without it here the request was dropped.
  useEffect(() => {
    const onSendPrompt = (e: Event) => {
      const d = (e as CustomEvent).detail as { project?: string; prompt?: string } | undefined
      if (!d || d.project !== project || !d.prompt) return
      setPanelOpen(true)
      handleSend(d.prompt, [], { handedOff: true })
    }
    window.addEventListener('twasta:agent-send-prompt', onSendPrompt as EventListener)
    window.addEventListener('twasta:agent-run-task', onSendPrompt as EventListener)
    return () => {
      window.removeEventListener('twasta:agent-send-prompt', onSendPrompt as EventListener)
      window.removeEventListener('twasta:agent-run-task', onSendPrompt as EventListener)
    }
  }, [project, handleSend, setPanelOpen])

  if (!visible) return null

  const hasActivity = agent.messages.length > 0 || agent.isStreaming || agent.todos.length > 0
  const latestToolMessage = [...agent.messages].reverse().find((m) => m.role === 'tool') || null

  // ONE live label. A `tool_activity` event sets BOTH the tool message and the
  // progress status; the progress status is always the NEWER of the two — a
  // CLI-level status ("API throttled", "compacting") arrives after the tool
  // line — so it wins the label; the tool's detail half only belongs to it
  // when they agree.
  const liveLabel =
    friendlyStatus(agent.agentProgress?.status) || latestToolMessage?.tool
  const liveDetail =
    latestToolMessage && liveLabel === latestToolMessage.tool
      ? latestToolMessage.file
      : undefined

  // Map the Claude agent's TodoWrite plan onto the shared tracker shape. It
  // renders as the pinned plan bar above the message box, not in the chat.
  const trackerTasks: TrackerTask[] = agent.todos.map((t, i) => ({
    id: String(i),
    label: t.content,
    status: CLAUDE_TODO_STATUS[t.status] || 'pending',
  }))
  const checklistFooter = trackerTasks.length > 0
    ? <ChecklistFooter tasks={trackerTasks} isStreaming={agent.isStreaming} open={checklistOpen} onToggle={() => setChecklistOpen((v) => !v)} />
    : undefined

  const body = (
    <>
      {!hasActivity && <EmptyHint />}

      <CliConversation
        messages={agent.messages}
        isStreaming={agent.isStreaming}
        liveLabel={liveLabel}
        liveDetail={liveDetail}
        elapsedS={agent.agentProgress?.elapsed}
        onPermissionResponse={agent.respondToPermission}
        onAnswer={(ans, connectionId) => {
          // A CLI answer is prose, so the connection id has to travel IN the
          // text — appended in a canonical form the agent can copy without
          // re-casing it (a mangled id silently binds to the project's own
          // database instead of the one the user just defined).
          const text = withConnectionMarker(ans, connectionId)
          agent.resolveAsk(text); handleSend(text, [], { internal: true })
        }}
        project={project}
      />

      {agent.error && <DockAlert level="error">{agent.error}</DockAlert>}
    </>
  )

  // Scroll signal for DockShell: bumps when messages, plan, or progress changes.
  const completedTasks = trackerTasks.filter((t) => t.status === 'completed').length
  const contentSignal =
    agent.messages.length * 1000 +
    trackerTasks.length * 10 +
    completedTasks +
    (agent.agentProgress?.elapsed || 0) +
    (agent.isStreaming ? 1 : 0)

  // One-line status text: the in-progress task, then tool name + file, then
  // progress status, then a generic fallback.
  const runningTask = trackerTasks.find((t) => t.status === 'running')
  const transientStatus =
    runningTask?.label
    || (liveLabel ? [liveLabel, liveDetail].filter(Boolean).join(' · ') : null)
    || (agent.isStreaming ? 'Agent is working…' : null)

  const hasHistory = agent.messages.length > 0 || agent.todos.length > 0

  return (
    <>
    <DockShell
      projectTitle={projectTitle}
      project={project}
      wsStatus={agent.wsStatus}
      panelOpen={panelOpen}
      setPanelOpen={setPanelOpen}
      maximized={maximized}
      setMaximized={setMaximized}
      isStreaming={agent.isStreaming}
      onSend={handleSend}
      onCancel={agent.cancelTask}
      panelBody={body}
      plansSlot={
        <DockPlansSlot
          project={project}
          backend="claude"
          busy={agent.isStreaming}
          onResumeOwn={agent.resumePlan}
          onPauseOwn={agent.cancelTask}
          refreshSignal={agent.isStreaming}
          onCompletedCount={setCompletedPlanCount}
        />
      }
      transientStatus={transientStatus}
      contentSignal={contentSignal}
      hasHistory={hasHistory}
      panelFooter={checklistFooter}
      onNewChat={agent.clearMessages}
      completedPlanCount={completedPlanCount}
      onContinueOnPhone={() => setPhoneHandoffOpen(true)}
    />
    {phoneHandoffOpen && (
      <ContinueOnPhoneModal
        open={phoneHandoffOpen}
        onClose={() => setPhoneHandoffOpen(false)}
        project={project}
      />
    )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Codex Agent dock — codex mode. Same shell + UX as ClaudeAgentDock; the
// dispatch path is the new /api/codexagent WebSocket. Codex runs
// autonomously (`--ask-for-approval never --sandbox workspace-write`) so
// there is no permission-prompt rendering and no TodoWrite tracker.
// ---------------------------------------------------------------------------

function CodexAgentDock({
  project, projectTitle, onHandoffToOrchestrator, engine = 'codex',
}: RouterProps & {
  onHandoffToOrchestrator?: (msg: string) => void
  /** 'opencode' runs the same dock against /api/opencodeagent. */
  engine?: 'codex' | 'opencode'
}) {
  const location = useLocation()
  const visible = isProjectContext(location.pathname)

  const panelOpen = useDockUiStore((s) => s.panelOpen)
  const setPanelOpen = useDockUiStore((s) => s.setPanelOpen)
  const maximized = useDockUiStore((s) => s.maximized)
  const setMaximized = useDockUiStore((s) => s.setMaximized)
  const [completedPlanCount, setCompletedPlanCount] = useState(0)

  const agent = useCodexAgent({ project, engine })
  // "Continue on phone" QR hand-off dialog (the phone app runs this dock too).
  const [phoneHandoffOpen, setPhoneHandoffOpen] = useState(false)

  // Broadcast file-change after the agent finishes so previews refresh.
  // Same conservative trigger as ClaudeAgentDock — fire on any
  // streaming→idle transition since the backend doesn't always report
  // touched files for every task type.
  const prevStreamingRef = useRef(false)
  useEffect(() => {
    if (prevStreamingRef.current && !agent.isStreaming) {
      window.dispatchEvent(
        new CustomEvent('twasta:agent-files-changed', {
          detail: {
            project,
            files: agent.changedFiles.map((f) => f.path),
            // Deleted paths are carried SEPARATELY: a surface showing a
            // deleted object must evict it, not refetch it (a refetch 404s and
            // leaves the stale object on screen). See useAgentRefresh.
            deleted: agent.changedFiles.filter((f) => f.deleted).map((f) => f.path),
          },
        }),
      )
    }
    prevStreamingRef.current = agent.isStreaming
  }, [agent.isStreaming, agent.changedFiles, project])

  const handleSend = useCallback(async (
    text: string,
    attachments: Attachment[],
    // internal: the text was NOT typed by the user (clicked question answer,
    // Unit-Test button prompt) — logged for cost but kept out of ↑ recall.
    // handedOff: a ready brief from another screen (Testing "Fix with AI", a
    // designer's hand-off) — it runs as written, never re-routed by its wording.
    opts?: { internal?: boolean; handedOff?: boolean },
  ) => {
    // Generation (build all / missing / remaining objects) always runs through
    // the orchestrator (plan → per-object review), never this free-form change
    // agent which would author every object inline with no plan/review. Not
    // for a handed-off brief: a test-fix brief's "all pending approvals" /
    // "never create …" read as a bulk build and started building objects.
    if (onHandoffToOrchestrator && !opts?.handedOff && isBuildAllRequest(text)) {
      onHandoffToOrchestrator(text)
      return
    }
    const chat_history = agent.messages
      .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'ask')
      .map((m) => ({
        role: m.role === 'ask' ? 'assistant' : m.role,
        content: m.role === 'ask' ? (m.ask?.question || m.content) : m.content,
      }))
    const openObject = useBuilderContextStore.getState().openObject
    await agent.startTask('project_modify', {
      user_input: text,
      description: text,
      project_name: projectTitle,
      chat_history,
      ...(opts?.internal ? { _internal_prompt: true } : {}),
      ...(openObject ? { current_object: openObject } : {}),
      ...(attachments.length ? { attachments: attachments.map(toWire) } : {}),
    })
  }, [agent, projectTitle, onHandoffToOrchestrator])

  // Clarifying questions must surface the panel so the user can answer.
  useEffect(() => {
    if (
      visible &&
      !panelOpen &&
      agent.messages.some((m) => m.role === 'ask' && !m.askAnswered)
    ) {
      setPanelOpen(true)
    }
  }, [visible, panelOpen, agent.messages])

  // Builder Mode "Create" hands an ungenerated object off to the agent —
  // same shape as the Claude dock so the same trigger event works for
  // either backend.
  useEffect(() => {
    const onCreateObject = (e: Event) => {
      const d = (e as CustomEvent).detail as CreateObjectDetail | undefined
      if (!d || d.project !== project || !d.objName || !d.objType) return
      const taskType = d.objType === 'V' ? 'visual' : d.objType === 'S' ? 'page' : 'model'
      setPanelOpen(true)
      agent.startTask(taskType, {
        obj_name: d.objName,
        obj_type: d.objType,
        description: d.description || d.functionalSpecs || d.title || d.objName,
        functional_specs: d.functionalSpecs || '',
        project_folder: project,
        project_name: projectTitle,
        ...(d.designReferenceUrl ? { design_reference_url: d.designReferenceUrl } : {}),
      })
    }
    window.addEventListener('twasta:agent-create-object', onCreateObject as EventListener)
    return () => window.removeEventListener('twasta:agent-create-object', onCreateObject as EventListener)
  }, [project, projectTitle, agent])

  // The Transaction Designer's "Unit Test" button routes here — the agent
  // delegates to the unit-test-runner sub-agent (Claude) or runs the same
  // data-first protocol inline (Codex), asking before any model change. Reuses
  // handleSend so chat history / open object are included.
  useEffect(() => {
    const onRunUnitTest = (e: Event) => {
      const d = (e as CustomEvent).detail as RunUnitTestDetail | undefined
      if (!d || d.project !== project || !d.objName) return
      setPanelOpen(true)
      handleSend(buildUnitTestPrompt(d), [], { internal: true })
    }
    window.addEventListener('twasta:agent-run-unit-test', onRunUnitTest as EventListener)
    return () => window.removeEventListener('twasta:agent-run-unit-test', onRunUnitTest as EventListener)
  }, [project, handleSend, setPanelOpen])

  // A designer handing a ready brief to the agent (e.g. the Playbook
  // designer's "Build in Ask-AI" for an IOFlow flow it cannot write itself).
  // `twasta:agent-run-task` (Testing "Fix with AI", the Database Designer's
  // dialect migration) is the same hand-off; the AutoAgent dock runs it as a
  // plan, this dock as a turn — without it here the request was dropped.
  useEffect(() => {
    const onSendPrompt = (e: Event) => {
      const d = (e as CustomEvent).detail as { project?: string; prompt?: string } | undefined
      if (!d || d.project !== project || !d.prompt) return
      setPanelOpen(true)
      handleSend(d.prompt, [], { handedOff: true })
    }
    window.addEventListener('twasta:agent-send-prompt', onSendPrompt as EventListener)
    window.addEventListener('twasta:agent-run-task', onSendPrompt as EventListener)
    return () => {
      window.removeEventListener('twasta:agent-send-prompt', onSendPrompt as EventListener)
      window.removeEventListener('twasta:agent-run-task', onSendPrompt as EventListener)
    }
  }, [project, handleSend, setPanelOpen])

  if (!visible) return null

  const latestToolMessage = [...agent.messages].reverse().find((m) => m.role === 'tool') || null

  const hasActivity = agent.messages.length > 0 || agent.isStreaming

  // Same single live label as the Claude dock — see the comment there for why
  // the progress status wins the label and the tool detail is conditional.
  const liveLabel =
    friendlyStatus(agent.agentProgress?.status) || latestToolMessage?.tool
  const liveDetail =
    latestToolMessage && liveLabel === latestToolMessage.tool
      ? latestToolMessage.file
      : undefined

  const body = (
    <>
      {!hasActivity && <EmptyHint />}

      <CliConversation
        // useCodexAgent's message shape omits `permissionRequest`/
        // `permissionDecision`; the shared renderer treats those as optional.
        messages={agent.messages}
        isStreaming={agent.isStreaming}
        liveLabel={liveLabel}
        liveDetail={liveDetail}
        elapsedS={agent.agentProgress?.elapsed}
        onPermissionResponse={agent.respondToPermission}
        onAnswer={(ans, connectionId) => {
          // A CLI answer is prose, so the connection id has to travel IN the
          // text — appended in a canonical form the agent can copy without
          // re-casing it (a mangled id silently binds to the project's own
          // database instead of the one the user just defined).
          const text = withConnectionMarker(ans, connectionId)
          agent.resolveAsk(text); handleSend(text, [], { internal: true })
        }}
        agentLabel={AGENT_DISPLAY_NAME}
        project={project}
      />

      {agent.error && <DockAlert level="error">{agent.error}</DockAlert>}
    </>
  )

  const contentSignal =
    agent.messages.length * 1000
    + (agent.agentProgress?.elapsed || 0)
    + (agent.isStreaming ? 1 : 0)

  const transientStatus =
    (liveLabel ? [liveLabel, liveDetail].filter(Boolean).join(' · ') : null)
    || (agent.isStreaming ? `${AGENT_DISPLAY_NAME} is working…` : null)

  const hasHistory = agent.messages.length > 0

  return (
    <>
    <DockShell
      projectTitle={projectTitle}
      project={project}
      wsStatus={agent.wsStatus}
      panelOpen={panelOpen}
      setPanelOpen={setPanelOpen}
      maximized={maximized}
      setMaximized={setMaximized}
      isStreaming={agent.isStreaming}
      onSend={handleSend}
      onCancel={agent.cancelTask}
      panelBody={body}
      plansSlot={
        <DockPlansSlot
          project={project}
          backend={engine}
          busy={agent.isStreaming}
          onResumeOwn={agent.resumePlan}
          onPauseOwn={agent.cancelTask}
          refreshSignal={agent.isStreaming}
          onCompletedCount={setCompletedPlanCount}
        />
      }
      transientStatus={transientStatus}
      contentSignal={contentSignal}
      hasHistory={hasHistory}
      onNewChat={agent.clearMessages}
      completedPlanCount={completedPlanCount}
      onContinueOnPhone={() => setPhoneHandoffOpen(true)}
    />
    {phoneHandoffOpen && (
      <ContinueOnPhoneModal
        open={phoneHandoffOpen}
        onClose={() => setPhoneHandoffOpen(false)}
        project={project}
      />
    )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Shared small components
// ---------------------------------------------------------------------------

/**
 * The CLI agents' conversation (Claude, Codex, OpenCode), read as TURNS: each
 * user message starts one. Inside a turn the user's message, the agent's
 * replies, questions, permission requests and warnings stay at full weight;
 * tool chatter and progress lines fold into "Show steps"; the "Agent turn
 * completed" accounting line and the "Updated N files" note become the quiet
 * "Took … · N files changed · Details" footer. While the last turn runs, ONE
 * working card shows what the agent is doing now.
 */
function CliConversation({
  messages, isStreaming, liveLabel, liveDetail, elapsedS,
  onPermissionResponse, onAnswer, agentLabel, project,
}: {
  messages: CliMessage[]
  isStreaming: boolean
  liveLabel?: string
  liveDetail?: string
  elapsedS?: number
  onPermissionResponse?: (permissionId: string, decision: 'allow' | 'deny') => void
  onAnswer?: (answer: string, connectionId?: string) => void
  agentLabel?: string
  project?: string
}) {
  const turns = groupTurns(messages, (m) => m.role === 'user')
  const working = (steps: DockStep[]) => (
    <WorkingCard
      label={liveLabel || 'Working on it…'}
      detail={liveDetail}
      elapsedS={elapsedS}
      steps={dedupeSteps(steps)}
    />
  )
  return (
    <>
      {turns.map((turn, ti) => {
        const nodes: ReactNode[] = []
        const steps: DockStep[] = []
        let usage: TurnUsage | null = null
        let files: string[] = []
        for (const [i, m] of turn.entries()) {
          const key = `${ti}-${i}`
          if (m.role === 'tool') {
            const text = [m.tool, m.file].filter(Boolean).join(' — ')
            if (text) steps.push({ text })
            continue
          }
          if (m.role === 'activity') {
            if (m.activityLevel === 'warning') {
              nodes.push(<DockAlert key={key} level="warning">{m.content}</DockAlert>)
            } else if (/^agent turn completed/i.test(m.content)) {
              usage = { elapsedS: m.meta?.elapsedS, tokens: m.meta?.tokens, costUsd: m.meta?.costUsd }
            } else {
              steps.push({ text: m.content, elapsedS: m.meta?.elapsedS })
            }
            continue
          }
          if (m.role === 'system') {
            const changed = /^Updated \d+ files?: ([\s\S]*)$/.exec(m.content)
            if (changed) {
              files = changed[1].split(', ').map((f) => f.trim()).filter(Boolean)
              continue
            }
          }
          nodes.push(
            <AgentMessage
              key={key}
              message={m}
              onPermissionResponse={onPermissionResponse}
              onAnswer={onAnswer}
              agentLabel={agentLabel}
              project={project}
            />,
          )
        }
        const isWorking = ti === turns.length - 1 && isStreaming
        return (
          <div key={ti} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {nodes}
            {isWorking
              ? working(steps)
              : <TurnFooter usage={usage} steps={dedupeSteps(steps)} files={files} />}
          </div>
        )
      })}
      {isStreaming && turns.length === 0 && working([])}
    </>
  )
}

/** The CLI agent's TodoWrite checklist as the pinned plan bar. */
function ChecklistFooter({
  tasks, isStreaming, open, onToggle,
}: {
  tasks: TrackerTask[]
  isStreaming: boolean
  open: boolean
  onToggle: () => void
}) {
  const done = tasks.filter((t) => t.status === 'completed').length
  const total = tasks.length
  const running = tasks.find((t) => t.status === 'running')
  const allDone = done === total
  return (
    <PlanProgressCard
      title={running?.label || (allDone ? 'Checklist complete' : 'Checklist')}
      subtitle={`${done} of ${total} done`}
      progress={total ? (done / total) * 100 : 0}
      tone={allDone ? 'done' : isStreaming ? 'running' : 'paused'}
      open={open}
      onToggle={onToggle}
    >
      <TaskList tasks={tasks} />
    </PlanProgressCard>
  )
}

// The stream's own coarse statuses arrive as raw one-word states; every other
// status is already a plain-English sentence built by the backend's humanizer,
// so anything unmapped passes through untouched.
const RAW_STATUS_LABELS: Record<string, string> = {
  thinking: 'Thinking it through…',
  responding: 'Writing the reply…',
  awaiting_permission: 'Waiting for your approval…',
  working: 'Working on the app…',
  read: 'Reviewing the app',
  write: 'Writing changes',
  edit: 'Updating the app',
  glob: 'Looking through the app',
  grep: 'Searching the app',
  bash: 'Running a step on the app',
}

function friendlyStatus(status?: string): string | undefined {
  if (!status) return undefined
  return RAW_STATUS_LABELS[status.trim().toLowerCase()] || status
}

// A suggestion on the empty dock fills the message box (DockShell listens) with
// a sentence to finish — it never sends on its own. The event name lives in
// store/dockBridgeStore so the Workbench's "Ask for a change" uses the same one.

const STARTER_PROMPTS: Array<{ label: string; icon: ReactNode; text: string }> = [
  {
    label: 'Add a field to a form',
    icon: <FileText size={18} />,
    text: 'Add a field to the [form name] form called [field name]. It should hold [what goes in it].',
  },
  {
    label: 'Create a report or dashboard',
    icon: <BarChart3 size={18} />,
    text: 'Create a [report / dashboard] that shows [what you want to see], grouped by [how to break it down].',
  },
  {
    label: 'Add an approval step',
    icon: <CheckCircle2 size={18} />,
    text: 'Before a [record] is saved, it needs approval from [role]. ',
  },
  {
    label: "Fix something that isn't working",
    icon: <Search size={18} />,
    text: 'On the [screen name] screen, [what happens] — it should [what you expected].',
  },
]

/** A question the agent asked, as it stays in the conversation once it is no
 *  longer the open card: what was asked, and — if the user moved on — that it
 *  was withdrawn. */
function AskRecord({ text, state }: { text: string; state: 'open' | 'answered' | 'withdrawn' }) {
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: '0.25rem',
        padding: '0.5rem 0.75rem',
        borderLeft: '3px solid var(--color-accent)',
        background: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-md)',
        opacity: state === 'withdrawn' ? 0.7 : 1,
      }}
    >
      <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
        Question
      </span>
      <AssistantText text={text} />
      {state === 'withdrawn' && (
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          Not answered — you sent a different instruction.
        </span>
      )}
    </div>
  )
}

/** Above the open question card: the work is stopped until it is answered,
 *  and for a question parked across a restart, since when. */
function WaitingNote({ parked, askedAt }: { parked: boolean; askedAt?: number }) {
  const since = askedAt
    ? new Date(askedAt * 1000).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : null
  return (
    <div
      role="status"
      style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}
    >
      {parked
        ? `The work stopped here waiting for your answer${since ? ` (asked ${since})` : ''}. `
          + 'Answer it to carry on — or type a different instruction.'
        : 'Waiting for your answer — nothing is lost if you step away. '
          + 'Answer when you are back, or type "continue" to see the question again.'}
    </div>
  )
}

function EmptyHint() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '1.5rem 0.25rem 0.5rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div
          aria-hidden="true"
          style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, var(--gr-drkblue), var(--gr-drkgreen))',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
          }}
        >
          <Sparkles size={22} />
        </div>
        <h2 style={{ margin: '0.5rem 0 0', fontSize: '1.4375rem', lineHeight: 1.3, fontWeight: 600 }}>
          What would you like to change?
        </h2>
        <p style={{ margin: 0, fontSize: '1.0625rem', lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
          Describe it in your own words. The agent explains its plan before it changes anything.
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          Try one of these
        </span>
        {STARTER_PROMPTS.map((p) => (
          <button
            key={p.label}
            type="button"
            className="askai-suggestion"
            onClick={() => window.dispatchEvent(new CustomEvent(DOCK_PREFILL_EVENT, { detail: { text: p.text } }))}
          >
            <span aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--color-accent)', flexShrink: 0 }}>{p.icon}</span>
            {p.label}
          </button>
        ))}
      </div>
      <p style={{ margin: 0, fontSize: '1rem', color: 'var(--color-text-secondary)' }}>
        Tip: use <strong style={{ color: 'var(--color-text)' }}>+</strong> to attach a screenshot, a document or a database.
      </p>
    </div>
  )
}

// Status dot shown inside the model chip — solid when idle/ready, with an
// animated expanding "ping" halo while the agent is processing (or the socket
// is still connecting). Doubles the model selector as the activity indicator.
function StatusDot({ color, active }: { color: string; active: boolean }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', width: 8, height: 8, flexShrink: 0 }}>
      {active && (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: color,
            animation: 'ai-ping 1.3s cubic-bezier(0,0,0.2,1) infinite',
          }}
        />
      )}
      <span
        style={{
          position: 'relative',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          animation: active ? 'pulse 1.3s ease-in-out infinite' : undefined,
        }}
      />
    </span>
  )
}

// Model selector chip — lives in the header next to "Ask AI". Shows the config
// the dock will run for project_modify plus a live idle/processing dot, a
// model/thinking dot (color = model family, size = thinking level), and
// opens ModelSettingsModal on click. Re-fetches when the modal dispatches
// `twasta:settings-changed`; the dot also updates from the backend's
// resolved `agent_config` event at each run start.
function ModelChip({
  isStreaming = false,
  wsStatus = 'connected',
}: {
  isStreaming?: boolean
  wsStatus?: ConnectionStatus
}) {
  const [open, setOpen] = useState(false)

  // The header's ⋯ menu ("Model and effort") opens the same settings dialog.
  useEffect(() => {
    const handler = () => setOpen(true)
    window.addEventListener(OPEN_MODEL_SETTINGS_EVENT, handler)
    return () => window.removeEventListener(OPEN_MODEL_SETTINGS_EVENT, handler)
  }, [])
  const [bundle, setBundle] = useState<LlmSettingsBundle | null>(null)
  // The backend's RESOLVED model/effort for the CURRENT run, for ANY task
  // type — ground truth for the model dot and, while a run is live, the label
  // (covers legacy-matrix and platform-default resolution the bundle alone
  // can't show). Carries the task_type so the header can name which task is
  // running on which model (the whole point of per-task model routing).
  const [resolvedRun, setResolvedRun] = useState<
    { taskType: string; model: string; effort: string } | null
  >(null)
  // What the CLI actually reported using on the LAST completed run — may list
  // more than one model (the CLI's own internal helper model + the configured
  // one). Used to reconcile "configured" vs "actually ran".
  const [lastActual, setLastActual] = useState<
    { taskType: string; actualModel: string } | null
  >(null)

  const load = useCallback(() => {
    getLlmBundle()
      .then(setBundle)
      .catch(() => setBundle(null))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const handler = () => load()
    window.addEventListener('twasta:settings-changed', handler)
    return () => window.removeEventListener('twasta:settings-changed', handler)
  }, [load])

  useEffect(() => {
    // Any task_type (not just project_modify): the user configures different
    // models per task, so the chip should reflect whichever task is running —
    // that's how they visually confirm the model actually switched.
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {}
      setResolvedRun({
        taskType: d.task_type || '',
        model: d.model || '',
        effort: d.effort || '',
      })
    }
    window.addEventListener('twasta:agent-config', handler)
    return () => window.removeEventListener('twasta:agent-config', handler)
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {}
      if (!d.actual_model) return
      setLastActual({ taskType: d.task_type || '', actualModel: d.actual_model })
    }
    window.addEventListener('twasta:agent-actual-model', handler)
    return () => window.removeEventListener('twasta:agent-actual-model', handler)
  }, [])

  // The active config for project_modify (per-task override wins, falling
  // back to the scope default) — drives both the label and the model dot.
  const activeCfg = (() => {
    if (!bundle) return null
    const overrideName = bundle.task_overrides?.project_modify || ''
    const activeName = overrideName || bundle.default_config_name || ''
    if (!activeName) return null
    return bundle.configs.find((c) => c.name === activeName) || null
  })()

  // A per-task routing override for project_modify silently BEATS the default
  // config — the usual reason "I changed my default LLM but the dock still runs
  // the old one". Name it in the tooltip so the precedence is discoverable
  // instead of looking like the default was ignored.
  const overrideNote = (() => {
    const overrideName = bundle?.task_overrides?.project_modify || ''
    if (!overrideName) return ''
    const def = bundle?.default_config_name || ''
    return `\nPinned by the "Ask AI / project change" task routing override → “${overrideName}”${
      def && def !== overrideName
        ? `, so your default configuration (“${def}”) does not apply here. Clear that override in Settings → LLM to follow the default.`
        : '.'
    }`
  })()

  // Resting label: the configured project_modify model, shown when no run is
  // in flight. This is the dock's "default" identity.
  const restingLabel = (() => {
    if (!bundle) return '…'
    if (bundle.configs.length === 0) {
      // No own configs — the user rides the platform default, which only an
      // instance admin can change.
      return resolvedRun?.model
        ? `Platform default · ${resolvedRun.model}`
        : 'Platform default'
    }
    const overrideName = bundle.task_overrides?.project_modify || ''
    const activeName = overrideName || bundle.default_config_name || ''
    if (!activeName) return 'No config'
    const cfg = activeCfg
    const modelId =
      cfg?.mode === 'api' ? cfg.model : cfg?.mode === 'agent' ? cfg.model : ''
    if (!modelId) return activeName
    // Show the full configuration detail rather than just the bare model name:
    // the config's own name plus its full model id (e.g. "Fast Coder ·
    // gpt-5.5"), so the chip describes the configuration the dock will run.
    // When the config name is just the model (no custom name) we avoid the
    // redundant "gpt-5.5 · gpt-5.5" and show it once.
    const short = shortModelLabel(modelId)
    return activeName && activeName !== short && activeName !== modelId
      ? `${activeName} · ${modelId}`
      : modelId
  })()

  // Live label: while a run is in flight, name the task being run and the
  // model resolved FOR THAT TASK — so a user who routed, say, FRS to Opus and
  // BL to Haiku literally sees "FRS · opus…" / "Business logic · haiku…" as
  // each phase runs. This is the visual confirmation of per-task switching.
  const liveLabel = (() => {
    if (!resolvedRun?.model) return ''
    const t = friendlyTaskLabel(resolvedRun.taskType)
    const m = shortModelLabel(resolvedRun.model)
    return t ? `${t} · ${m}` : m
  })()

  const label = isStreaming && liveLabel ? liveLabel : restingLabel

  // Thinking level for the tooltip: while a run is live, the running task's
  // resolved effort; when idle, the active config's.
  const dotThinking = (isStreaming ? resolvedRun?.effort : '') || activeCfg?.thinking || ''

  // Reconciliation note (idle only): what the CLI actually reported using on
  // the last run. Shown when it lists a model the user did NOT configure
  // (e.g. the CLI's own internal Haiku helper) so a surprising billing line
  // has a visible, non-alarming explanation right in the header.
  // Compare against the model resolved for the task that ACTUALLY ran
  // (resolvedRun, set by that run's agent_config just before it) — not the
  // project_modify config — so a task legitimately routed to a different
  // model isn't falsely flagged as "extra".
  const configuredModel = resolvedRun?.model || activeCfg?.model || ''
  const ranModels = (lastActual?.actualModel || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const extraRanModels = ranModels.filter(
    (m) => shortModelLabel(m) !== shortModelLabel(configuredModel),
  )
  const showActualNote = !isStreaming && ranModels.length > 0 && extraRanModels.length > 0

  // The dot folds in both the WebSocket health and the live run state — accent
  // (animated) while processing, green when ready, amber/red for the socket.
  const active = isStreaming || wsStatus === 'connecting'
  const dotColor = isStreaming
    ? 'var(--color-accent)'
    : wsStatus === 'connected'
    ? 'var(--color-success)'
    : wsStatus === 'connecting'
    ? 'var(--color-warning)'
    : wsStatus === 'error'
    ? 'var(--color-error)'
    : 'var(--color-text-secondary)'
  const stateText = isStreaming
    ? 'Processing'
    : wsStatus === 'connected'
    ? 'Ready'
    : wsStatus === 'connecting'
    ? 'Connecting'
    : wsStatus === 'error'
    ? 'Connection error'
    : 'Idle'

  const actualNote = showActualNote
    ? `\nLast run${lastActual?.taskType ? ` (${friendlyTaskLabel(lastActual.taskType)})` : ''} used: ${ranModels
        .map(shortModelLabel)
        .join(', ')}. ${extraRanModels
        .map(shortModelLabel)
        .join(', ')} ${extraRanModels.length === 1 ? 'was' : 'were'} added by the CLI's own internal steps — not from your config.`
    : ''

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`${stateText} · ${label}${dotThinking ? ` · thinking: ${dotThinking}` : ''}${overrideNote}${actualNote} — click to change agent model`}
        aria-label={`${stateText}. Model: ${label}. Change agent model`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          alignSelf: 'flex-start',
          maxWidth: '100%',
          minWidth: 0,
          padding: '1px 0',
          background: 'none',
          color: 'var(--color-text-secondary)',
          border: 'none',
          fontSize: '0.9375rem',
          fontWeight: 400,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <StatusDot color={dotColor} active={active} />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        {showActualNote && (
          // The last run reported extra models the user didn't configure
          // (the CLI's own internal helper). A muted info glyph flags it
          // without alarming — the full detail is in the tooltip.
          <Info
            size={13}
            aria-hidden="true"
            style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }}
          />
        )}
        <ChevronDown size={13} aria-hidden="true" style={{ flexShrink: 0 }} />
      </button>
      <ModelSettingsModal open={open} onClose={() => setOpen(false)} />
    </>
  )
}

// Fired by the ⋯ menu's "Model and effort" item; the header ModelChip owns the
// settings dialog and opens it.
const OPEN_MODEL_SETTINGS_EVENT = 'twasta:open-model-settings'

// A square, icon-only header button with an accessible name.
function DockIconButton({
  label, onClick, disabled = false, active = false, children, ...rest
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
  children: ReactNode
  'aria-haspopup'?: 'menu'
  'aria-expanded'?: boolean
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        flexShrink: 0,
        background: active ? 'var(--color-accent-weak)' : 'transparent',
        color: active ? 'var(--color-accent)' : 'var(--color-text)',
        border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  )
}

// The header's ONE overflow menu — everything that isn't the conversation:
// model settings, finished plans, full screen, collapse, continue on phone.
function HeaderOverflowMenu({
  maximized, onToggleMax, onMinimise,
  showCompletedPlans, onToggleCompletedPlans, completedPlanCount = 0,
  onContinueOnPhone,
}: {
  maximized: boolean
  onToggleMax: () => void
  onMinimise: () => void
  showCompletedPlans?: boolean
  onToggleCompletedPlans?: () => void
  completedPlanCount?: number
  onContinueOnPhone?: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onDoc)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDoc)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (fn: () => void) => () => { setOpen(false); fn() }

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <DockIconButton
        label="More options"
        onClick={() => setOpen((o) => !o)}
        active={open}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </DockIconButton>
      {open && (
        <div
          role="menu"
          aria-label={`${DOCK_TITLE} options`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 30,
            width: 290,
            background: 'var(--color-raised)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 12px 32px var(--color-shadow)',
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MenuItem
            icon={<SlidersHorizontal size={17} />}
            label="Model and effort"
            hint="Choose which AI model runs the agent"
            onClick={pick(() => window.dispatchEvent(new CustomEvent(OPEN_MODEL_SETTINGS_EVENT)))}
          />
          {completedPlanCount > 0 && onToggleCompletedPlans && (
            <MenuItem
              icon={<ListChecks size={17} />}
              label={showCompletedPlans
                ? 'Hide finished plans'
                : `Show finished plans (${completedPlanCount})`}
              onClick={pick(onToggleCompletedPlans)}
            />
          )}
          <div role="separator" style={{ height: 1, background: 'var(--color-border)', margin: '6px 4px' }} />
          <MenuItem
            icon={maximized ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
            label={maximized ? 'Exit full screen' : 'Full screen'}
            hint={maximized ? 'Or press Esc' : undefined}
            onClick={pick(onToggleMax)}
          />
          <MenuItem
            icon={<PanelLeftClose size={17} />}
            label="Collapse to side bar"
            hint="Keeps working; shows status only"
            onClick={pick(onMinimise)}
          />
          {onContinueOnPhone && (
            <MenuItem
              icon={<Smartphone size={17} />}
              label="Continue on phone"
              hint="Follow this in the twasta Build app"
              onClick={pick(onContinueOnPhone)}
            />
          )}
        </div>
      )}
    </div>
  )
}

function MenuItem({
  icon, label, hint, onClick, disabled = false,
}: {
  icon: ReactNode
  label: string
  hint?: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      role="menuitem"
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--color-surface-2)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
      onFocus={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--color-surface-2)' }}
      onBlur={(e) => { e.currentTarget.style.background = 'transparent' }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minHeight: 44,
        padding: '0.5rem 0.75rem',
        background: 'transparent',
        color: 'var(--color-text)',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span aria-hidden="true" style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--color-text-secondary)' }}>{icon}</span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.35 }}>
        <span style={{ fontSize: '1rem', fontWeight: 500 }}>{label}</span>
        {hint && <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{hint}</span>}
      </span>
    </button>
  )
}

// Friendly short label for a model id — drops trailing dates / version
// suffixes so it fits in the chip.
function shortModelLabel(model: string): string {
  if (!model || model === 'default') return 'Default model'
  // Trim long date suffixes like -20251001
  const stripped = model.replace(/-\d{8}$/, '')
  return stripped
}

// Human name for a backend task_type, used in the header chip while a run is
// live so the user can see WHICH task the current model was resolved for.
const TASK_LABELS: Record<string, string> = {
  project_modify: 'Modify',
  frs: 'FRS',
  enhance_spec: 'FRS',
  enhance_frs: 'FRS',
  model: 'Data model',
  app_json: 'App',
  application: 'App',
  page: 'Page',
  visual: 'Visual',
  dashboard: 'Dashboard',
  schema: 'Schema',
  report: 'Report',
  workflow_generator: 'Workflow',
  code: 'Code',
  code_assist: 'Code',
  transaction_editor: 'Transaction',
  classify: 'Routing',
  onboarding_help: 'Help',
}
function friendlyTaskLabel(taskType: string): string {
  if (!taskType) return ''
  const known = TASK_LABELS[taskType]
  if (known) return known
  // Fallback: turn snake_case into Title Case.
  return taskType
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ')
}

// What a running object build is doing right now, in plain words — the one
// sub-line a plan row gets (it used to show Model ✓ · Logic … · Review 3/5 plus
// time and tokens for every row).
function runningStepText(stage?: string | null, isT = false, reviewIters?: number | null): string {
  const modelDone = stage === 'model_done' || stage === 'bl_done'
  const logicDone = stage === 'bl_done'
  if (!modelDone) return 'Designing it'
  if (isT && !logicDone) return 'Writing the business logic'
  return reviewIters ? `Checking it works (try ${reviewIters} of 5)` : 'Checking it works'
}

function TodoRow({ status, label, error, reviewIters, stage, objType, step }: {
  status: TodoStatus; label: string; error?: string | null; reviewIters?: number | null
  stage?: string | null; objType?: string | null; step?: string | null
}) {
  const icon =
    status === 'running' ? (
      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
    ) : status === 'completed' ? (
      <CheckCircle2 size={16} style={{ color: 'var(--color-success)' }} />
    ) : status === 'error' ? (
      <XCircle size={16} style={{ color: 'var(--color-error)' }} />
    ) : status === 'skipped' ? (
      <SkipForward size={16} style={{ color: 'var(--color-text-secondary)' }} />
    ) : (
      <Circle size={16} style={{ color: 'var(--color-text-faint)' }} />
    )
  const statusWord =
    status === 'running' ? 'In progress' : status === 'completed' ? 'Done'
      : status === 'error' ? 'Failed' : status === 'skipped' ? 'Skipped' : 'To do'
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.625rem',
        padding: '0.3rem 0',
        fontSize: '1rem',
        lineHeight: 1.45,
        color: status === 'skipped' ? 'var(--color-text-secondary)' : 'var(--color-text)',
        fontWeight: status === 'running' ? 600 : 400,
      }}
    >
      <span aria-label={statusWord} role="img" style={{ flexShrink: 0, marginTop: 2, display: 'inline-flex' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
        <div style={{ textDecoration: status === 'skipped' ? 'line-through' : undefined }}>{label}</div>
        {status === 'running' && step === 'object' && (
          <div style={{ fontSize: '0.9375rem', fontWeight: 400, color: 'var(--color-text-secondary)' }}>
            {runningStepText(stage, objType === 'T', reviewIters)}
          </div>
        )}
        {error && (
          <div style={{ fontSize: '0.9375rem', fontWeight: 400, color: 'var(--color-error)', marginTop: 2 }}>{error}</div>
        )}
      </div>
    </li>
  )
}

export interface TrackerTask {
  id: string
  label: string
  status: TodoStatus
  error?: string | null
  phaseId?: string | null
  reviewIters?: number | null
  // Granular build sub-step ('model_done' -> 'bl_done') + object kind, used to
  // say in plain words what the running object is doing.
  stage?: string | null
  objType?: string | null
  step?: string | null
}

export interface TrackerPhase {
  id: string
  title: string
}

// The plan's rows — status icon + name — shared by every dock so a plan looks
// the same whichever backend runs it. With `phases` (the build orchestrator)
// object tasks are grouped under their phase, each tagged Done / In progress /
// Next; otherwise a flat list. The caller owns the box (PlanProgressCard).
function TaskList({ tasks, phases }: { tasks: TrackerTask[]; phases?: TrackerPhase[] }) {
  if (tasks.length === 0) return null
  const usePhases = !!(phases && phases.length)
  const phaseIds = new Set((phases || []).map((p) => p.id))
  // Tasks without a phase (pipeline/deploy/sample) OR whose phase is unknown
  // still render at the top so nothing silently disappears in grouped view.
  const topTasks = usePhases ? tasks.filter((t) => !t.phaseId || !phaseIds.has(t.phaseId)) : tasks
  const row = (t: TrackerTask) => (
    <TodoRow key={t.id} status={t.status} label={t.label} error={t.error} reviewIters={t.reviewIters}
      stage={t.stage} objType={t.objType} step={t.step} />
  )
  const listStyle = { listStyle: 'none', margin: 0, padding: 0 } as const
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {topTasks.length > 0 && <ul style={listStyle}>{topTasks.map(row)}</ul>}
      {usePhases && phases!.map((ph) => {
        const ptasks = tasks.filter((t) => t.phaseId === ph.id)
        if (!ptasks.length) return null
        const done = ptasks.filter((t) => t.status === 'completed' || t.status === 'skipped').length
        const started = ptasks.some((t) => t.status !== 'pending')
        const tag = done === ptasks.length ? 'Done' : started ? 'In progress' : 'Next'
        const tagStyle =
          tag === 'Done'
            ? { color: 'var(--color-success)', background: 'var(--color-success-weak)' }
            : tag === 'In progress'
            ? { color: 'var(--color-accent)', background: 'var(--color-accent-weak)' }
            : { color: 'var(--color-text-secondary)', background: 'var(--color-secondary-btn)' }
        return (
          <section key={ph.id} aria-label={ph.title}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{ ...tagStyle, fontSize: '0.875rem', fontWeight: 600, padding: '1px 8px', borderRadius: 99, flexShrink: 0 }}>
                {tag}
              </span>
              <span style={{ fontWeight: 600, fontSize: '1rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ph.title}
              </span>
              <span style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', flexShrink: 0 }}>
                {done} of {ptasks.length}
              </span>
            </div>
            <ul style={listStyle}>{ptasks.map(row)}</ul>
          </section>
        )
      })}
    </div>
  )
}

// Maps the Claude agent's TodoWrite statuses onto the shared tracker vocabulary.
const CLAUDE_TODO_STATUS: Record<string, TodoStatus> = {
  pending: 'pending',
  in_progress: 'running',
  completed: 'completed',
}

// The clarifying-question bubble now lives in the shared AgentAskBubble
// component (used by all three Ask-AI backends).
